import type { Merge } from "type-fest";
import type { Exercise } from "./exercise-types";
import { ExerciseId } from "../../../domain/ids";

type RawExercise = Merge<Exercise, { id: string }>;

const RAW_EXERCISES: readonly [RawExercise, ...Array<RawExercise>] = [
  {
    id: "bench_press",
    dbId: "Barbell_Bench_Press_-_Medium_Grip",
    name: "Bench Press",
    primary: ["chest"],
    secondary: ["triceps", "shoulders"],
    equipment: "Barbell + bench",
    difficulty: "Intermediate",
    description:
      "Compound horizontal press performed lying on a flat bench. The gold standard for upper-body pressing strength.",
    cues: [
      "Plant feet flat; squeeze shoulder blades down and back into the bench",
      "Lower the bar to the mid-chest with a slight arc, elbows ~45° from torso",
      "Drive the bar up and slightly back toward your face, not straight up",
      "Keep wrists stacked over elbows; do not flare elbows wide",
    ],
    sets: "4 × 6–10 reps",
    rest: "2–3 min",
  },
  {
    id: "pushup",
    dbId: "Pushups",
    name: "Push-up",
    primary: ["chest"],
    secondary: ["triceps", "shoulders", "abs"],
    equipment: "Bodyweight",
    difficulty: "Beginner",
    description:
      "Bodyweight horizontal press. Trains the chest while demanding full-body bracing through the core.",
    cues: [
      "Hands just outside shoulder width, fingers spread",
      "Body forms a straight line from heels to head — no sagging hips",
      "Lower until chest is an inch from the floor, elbows ~45°",
      "Push the floor away; do not let the head dip forward",
    ],
    sets: "3 × AMRAP (as many as possible)",
    rest: "60–90 sec",
  },
  {
    id: "pullup",
    dbId: "Pullups",
    name: "Pull-up",
    primary: ["back"],
    secondary: ["biceps", "forearms"],
    equipment: "Pull-up bar",
    difficulty: "Intermediate",
    description:
      "Vertical pull using bodyweight. The premier lat-builder; few exercises develop back width as effectively.",
    cues: [
      "Grip slightly wider than shoulders, palms facing away",
      "Start from a dead hang with shoulders engaged (not slumped)",
      "Pull chest toward the bar — drive elbows down, not back",
      "Lower under control for a full 2-second negative",
    ],
    sets: "4 × 5–8 reps (use band assistance if needed)",
    rest: "2 min",
  },
  {
    id: "deadlift",
    dbId: "Barbell_Deadlift",
    name: "Deadlift",
    primary: ["erectors", "hamstrings", "glutes"],
    secondary: ["traps", "forearms", "quads", "back"],
    equipment: "Barbell",
    difficulty: "Advanced",
    description:
      "Hip-hinge lift from the floor. The most effective whole-body strength builder, but unforgiving of poor form.",
    cues: [
      "Bar over mid-foot; shins almost touching the bar",
      "Hips higher than knees, chest up, neutral spine",
      "Take slack out of the bar before pulling — no jerking",
      "Push the floor away; lock out by squeezing glutes, not leaning back",
    ],
    sets: "3–5 × 3–5 reps",
    rest: "3–5 min",
  },
  {
    id: "squat",
    dbId: "Bodyweight_Squat",
    name: "Squat",
    primary: ["quads", "glutes"],
    secondary: ["hamstrings", "abs"],
    equipment: "Barbell or bodyweight",
    difficulty: "Intermediate",
    description:
      "The king of lower-body exercises. Trains nearly every muscle below the waist plus a strong braced core.",
    cues: [
      "Feet shoulder-width, toes slightly out",
      "Brace the core as if expecting a punch before descending",
      "Knees track over toes; do not let them cave inward",
      "Descend until hip crease is below the knee, then drive up",
    ],
    sets: "4 × 5–8 reps",
    rest: "2–3 min",
  },
  {
    id: "lunges",
    dbId: "Dumbbell_Lunges",
    name: "Lunges",
    primary: ["quads", "glutes"],
    secondary: ["hamstrings", "calves"],
    equipment: "Bodyweight or dumbbells",
    difficulty: "Beginner",
    description:
      "Unilateral leg exercise that exposes and corrects side-to-side strength imbalances.",
    cues: [
      "Step forward into a long stride",
      "Front shin stays roughly vertical; back knee drops toward the floor",
      "Torso upright — do not lean forward over the front leg",
      "Push through the front heel to return to standing",
    ],
    sets: "3 × 10–12 reps per leg",
    rest: "60–90 sec",
  },
  {
    id: "bicep_curl",
    dbId: "Dumbbell_Alternate_Bicep_Curl",
    name: "Bicep Curl",
    primary: ["biceps"],
    secondary: ["forearms"],
    equipment: "Dumbbells or barbell",
    difficulty: "Beginner",
    description: "Isolation exercise for the biceps. Simple, but commonly ruined by momentum.",
    cues: [
      "Elbows pinned to your sides — do not let them drift forward",
      "Curl with the biceps, not by swinging the back",
      "Squeeze hard at the top for a 1-second peak contraction",
      "Lower for 2–3 seconds under control",
    ],
    sets: "3 × 8–12 reps",
    rest: "60 sec",
  },
  {
    id: "tricep_dip",
    dbId: "Bench_Dips",
    name: "Tricep Dip",
    primary: ["triceps"],
    secondary: ["chest", "shoulders"],
    equipment: "Parallel bars or bench",
    difficulty: "Intermediate",
    description: "Bodyweight push using the triceps to extend the elbows against load.",
    cues: [
      "Torso upright keeps focus on triceps; leaning forward shifts load to chest",
      "Lower until the upper arm is parallel to the floor",
      "Avoid bouncing at the bottom — control the descent",
      "Lock out elbows at the top without shrugging shoulders",
    ],
    sets: "3 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "overhead_press",
    dbId: "Standing_Military_Press",
    name: "Overhead Press",
    primary: ["shoulders"],
    secondary: ["triceps", "traps"],
    equipment: "Barbell or dumbbells",
    difficulty: "Intermediate",
    description:
      "Vertical press from shoulder to overhead. Builds true shoulder strength and overhead stability.",
    cues: [
      "Bar starts at the front of the shoulders, elbows under the bar",
      "Brace abs and glutes — do not arch the lower back excessively",
      "Press straight up; move head back slightly to let bar travel vertically",
      "Lock out overhead with biceps near ears, shoulders shrugged up",
    ],
    sets: "4 × 5–8 reps",
    rest: "2 min",
  },
  {
    id: "lateral_raise",
    dbId: "Side_Lateral_Raise",
    name: "Lateral Raise",
    primary: ["shoulders"],
    secondary: ["traps"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Isolation movement for the side delts — the key to wider-looking shoulders.",
    cues: [
      "Use light weights — momentum ruins this lift",
      "Slight bend in the elbows, locked throughout the set",
      "Lead with the elbows, not the hands",
      "Raise to shoulder height only; do not shrug at the top",
    ],
    sets: "3 × 12–15 reps",
    rest: "45–60 sec",
  },
  {
    id: "row",
    dbId: "Bent_Over_Barbell_Row",
    name: "Bent-Over Row",
    primary: ["back"],
    secondary: ["biceps", "traps", "forearms"],
    equipment: "Barbell or dumbbells",
    difficulty: "Intermediate",
    description:
      "Horizontal pull. Builds back thickness — a counterpart to pull-ups, which build width.",
    cues: [
      "Hinge to roughly 45° with a flat back and braced core",
      "Pull the bar to the lower chest / upper abs",
      "Drive elbows back, squeezing shoulder blades together",
      "Do not jerk with the lower back to start the rep",
    ],
    sets: "4 × 6–10 reps",
    rest: "2 min",
  },
  {
    id: "crunches",
    dbId: "Crunches",
    name: "Crunches",
    primary: ["abs"],
    secondary: ["obliques"],
    equipment: "Bodyweight",
    difficulty: "Beginner",
    description: "Spinal flexion exercise targeting the upper rectus abdominis.",
    cues: [
      "Lower back stays in contact with the floor",
      "Lift shoulder blades off the floor — do not yank on the neck",
      "Exhale forcefully at the top",
      "Lower slowly; no bouncing off the floor",
    ],
    sets: "3 × 15–25 reps",
    rest: "30–45 sec",
  },
  {
    id: "plank",
    dbId: "Plank",
    name: "Plank",
    primary: ["abs", "obliques"],
    secondary: ["shoulders", "glutes"],
    equipment: "Bodyweight",
    difficulty: "Beginner",
    description:
      "Isometric core hold. Trains anti-extension — resisting the lower back from sagging.",
    cues: [
      "Elbows directly under shoulders",
      "Straight line from heels to head — hips neither sagging nor piking",
      "Squeeze glutes and brace abs hard the entire hold",
      "Breathe steadily — do not hold your breath",
    ],
    sets: "3 × 30–60 sec hold",
    rest: "60 sec",
  },
  {
    id: "russian_twist",
    dbId: "Russian_Twist",
    name: "Russian Twist",
    primary: ["obliques"],
    secondary: ["abs"],
    equipment: "Bodyweight or weight plate",
    difficulty: "Beginner",
    description: "Rotational core exercise targeting the obliques.",
    cues: [
      "Sit with knees bent, lean back to ~45° with a long spine",
      "Rotate from the ribcage, not just by swinging the arms",
      "Touch the weight (or hands) to the floor beside each hip",
      "Keep chest tall — do not let the back round",
    ],
    sets: "3 × 20 total touches",
    rest: "45–60 sec",
  },
  {
    id: "calf_raise",
    dbId: "Standing_Calf_Raises",
    name: "Calf Raise",
    primary: ["calves"],
    secondary: [],
    equipment: "Bodyweight or dumbbells",
    difficulty: "Beginner",
    description: "Isolation exercise for the gastrocnemius and soleus via ankle plantarflexion.",
    cues: [
      "Rise onto the balls of the feet through the big toe",
      "Pause for 1 second at the top — squeeze",
      "Lower slowly through a full range — heels below toes if on a step",
      "Do not bounce; calves respond to time under tension",
    ],
    sets: "4 × 15–20 reps",
    rest: "60 sec",
  },
  {
    id: "hip_thrust",
    dbId: "Barbell_Hip_Thrust",
    name: "Hip Thrust",
    primary: ["glutes"],
    secondary: ["hamstrings"],
    equipment: "Barbell + bench",
    difficulty: "Intermediate",
    description: "Hip-extension exercise. Arguably the most effective glute builder available.",
    cues: [
      "Upper back on the bench, feet planted shoulder-width",
      "Tuck the chin and ribs down — neutral spine",
      "Drive hips up by squeezing glutes hard at lockout",
      "Shins vertical at the top; do not over-extend the lower back",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "leg_curl",
    dbId: "Lying_Leg_Curls",
    name: "Leg Curl",
    primary: ["hamstrings"],
    secondary: ["calves"],
    equipment: "Leg curl machine",
    difficulty: "Beginner",
    description: "Isolation exercise for the hamstrings via knee flexion.",
    cues: [
      "Align knee joint with the machine pivot",
      "Curl the heels toward the glutes with the hamstrings, not the lower back",
      "Squeeze at the top for 1 second",
      "Lower slowly — 3-second negative builds the most muscle",
    ],
    sets: "3 × 10–12 reps",
    rest: "60–90 sec",
  },
  {
    id: "shrugs",
    dbId: "Barbell_Shrug",
    name: "Shrugs",
    primary: ["traps"],
    secondary: ["forearms"],
    equipment: "Dumbbells or barbell",
    difficulty: "Beginner",
    description: "Isolation for the upper trapezius — scapular elevation under load.",
    cues: [
      "Stand tall, weights at your sides, arms relaxed",
      "Lift the shoulders straight up toward your ears",
      "Do not roll the shoulders — that adds wear without adding work",
      "Hold the top for 1 second, then lower under control",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "db_bench_press",
    dbId: "Dumbbell_Bench_Press",
    name: "Dumbbell Bench Press",
    primary: ["chest"],
    secondary: ["triceps", "shoulders"],
    equipment: "Dumbbells + bench",
    difficulty: "Intermediate",
    description:
      "Horizontal press with dumbbells. Allows a deeper stretch and trains each side independently.",
    cues: [
      "Plant feet, brace the core, squeeze shoulder blades back",
      "Press dumbbells up and slightly inward; they should not clank together",
      "Lower under control until elbows are level with the torso",
      "Keep wrists stacked over elbows",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "db_fly",
    dbId: "Dumbbell_Flyes",
    name: "Dumbbell Fly",
    primary: ["chest"],
    secondary: ["shoulders"],
    equipment: "Dumbbells + bench",
    difficulty: "Intermediate",
    description:
      "Chest isolation. Stretches and contracts the pecs without much triceps involvement.",
    cues: [
      "Soft bend in the elbows — lock the angle for the whole set",
      "Lower with the chest until you feel a strong stretch",
      "Pull arms back up in an arc, like hugging a barrel",
      "Use light weight; this is a stretch movement, not a press",
    ],
    sets: "3 × 10–12 reps",
    rest: "60–90 sec",
  },
  {
    id: "db_row",
    dbId: "One-Arm_Dumbbell_Row",
    name: "One-Arm Dumbbell Row",
    primary: ["back"],
    secondary: ["biceps", "traps", "forearms"],
    equipment: "Dumbbell + bench",
    difficulty: "Beginner",
    description: "Unilateral back exercise. Lets you pull heavy and target each side equally.",
    cues: [
      "Brace the non-working arm on the bench, flat back",
      "Pull the dumbbell to the hip, leading with the elbow",
      "Squeeze the lat at the top, then lower under full control",
      "Do not rotate the torso to gain leverage",
    ],
    sets: "3–4 × 8–12 reps per side",
    rest: "60–90 sec",
  },
  {
    id: "db_shoulder_press",
    dbId: "Dumbbell_Shoulder_Press",
    name: "Dumbbell Shoulder Press",
    primary: ["shoulders"],
    secondary: ["triceps", "traps"],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description:
      "Vertical press with dumbbells. Slightly safer on the shoulders than a barbell press.",
    cues: [
      "Start with dumbbells at shoulder level, palms forward",
      "Brace core and glutes to keep the lower back neutral",
      "Press up until arms are extended; do not clank weights at top",
      "Lower slowly — control on the way down builds strength",
    ],
    sets: "4 × 8–10 reps",
    rest: "90–120 sec",
  },
  {
    id: "db_front_raise",
    dbId: "Front_Dumbbell_Raise",
    name: "Front Dumbbell Raise",
    primary: ["shoulders"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description:
      "Isolation for the front delts. Most people overdo this if they already press heavy.",
    cues: [
      "Start with dumbbells in front of thighs, palms facing you",
      "Raise to shoulder height with straight (not locked) arms",
      "No swinging — if you need momentum, the weight is too heavy",
      "Lower for 2 seconds under control",
    ],
    sets: "3 × 12–15 reps",
    rest: "45–60 sec",
  },
  {
    id: "hammer_curl",
    dbId: "Hammer_Curls",
    name: "Hammer Curl",
    primary: ["biceps"],
    secondary: ["forearms"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Neutral-grip curl. Hits the brachialis and forearm in addition to the biceps.",
    cues: [
      "Palms face each other throughout the entire rep",
      "Pin elbows at your sides",
      "Curl with control; squeeze hard at the top",
      "Lower for 2–3 seconds before the next rep",
    ],
    sets: "3 × 8–12 reps",
    rest: "60 sec",
  },
  {
    id: "concentration_curl",
    dbId: "Concentration_Curls",
    name: "Concentration Curl",
    primary: ["biceps"],
    secondary: ["forearms"],
    equipment: "Dumbbell + bench",
    difficulty: "Beginner",
    description:
      "Bicep isolation, seated. Removes momentum and forces the bicep to do all the work.",
    cues: [
      "Sit, brace working elbow against the inside of the thigh",
      "Curl slowly — focus on the bicep contraction",
      "Squeeze hard at the top for 1 second",
      "Lower for a controlled 2–3 second negative",
    ],
    sets: "3 × 10–12 reps per arm",
    rest: "45 sec",
  },
  {
    id: "tricep_kickback",
    dbId: "Tricep_Dumbbell_Kickback",
    name: "Tricep Kickback",
    primary: ["triceps"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description:
      "Tricep isolation through elbow extension. Best done with strict form and light weight.",
    cues: [
      "Hinge at the hips ~45° with a flat back",
      "Pin upper arm to the torso, parallel to the floor",
      "Extend the forearm back until elbow is fully straight",
      "Squeeze the triceps; lower under control",
    ],
    sets: "3 × 10–12 reps",
    rest: "45–60 sec",
  },
  {
    id: "db_squat",
    dbId: "Dumbbell_Squat",
    name: "Dumbbell Squat",
    primary: ["quads", "glutes"],
    secondary: ["hamstrings", "abs"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description:
      "Squat with dumbbells held at the sides. Easier to bail out of than a barbell squat.",
    cues: [
      "Feet shoulder-width, toes slightly out",
      "Sit back as if into a chair, chest up",
      "Knees track over toes — do not let them cave in",
      "Descend to parallel or below, then drive up through the heels",
    ],
    sets: "3–4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "db_lunge",
    dbId: "Dumbbell_Lunges",
    name: "Dumbbell Lunge",
    primary: ["quads", "glutes"],
    secondary: ["hamstrings", "calves"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description:
      "Unilateral leg work loaded with dumbbells. Easier to add load than bodyweight lunges.",
    cues: [
      "Step forward into a long stride, torso upright",
      "Front shin near-vertical, back knee almost touching the floor",
      "Push through the front heel to return",
      "Do not let the front knee cave inward",
    ],
    sets: "3 × 10 reps per leg",
    rest: "60–90 sec",
  },
  {
    id: "db_side_bend",
    dbId: "Dumbbell_Side_Bend",
    name: "Dumbbell Side Bend",
    primary: ["obliques"],
    secondary: [],
    equipment: "Dumbbell",
    difficulty: "Beginner",
    description: "Oblique isolation through lateral spine flexion. Load only one side at a time.",
    cues: [
      "Hold a dumbbell in one hand at your side",
      "Bend sideways toward the loaded hand — do not lean forward or back",
      "Use the obliques to pull yourself back up",
      "Train both sides for equal reps",
    ],
    sets: "3 × 12–15 reps per side",
    rest: "45 sec",
  },
  {
    id: "db_shrug",
    dbId: "Dumbbell_Shrug",
    name: "Dumbbell Shrug",
    primary: ["traps"],
    secondary: ["forearms"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Trapezius isolation. Dumbbells allow a slightly larger range than a barbell.",
    cues: [
      "Stand tall, dumbbells at your sides, shoulders relaxed",
      "Shrug straight up toward the ears — do not roll the shoulders",
      "Pause for 1 second at the top",
      "Lower under control",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "incline_db_press",
    dbId: "Incline_Dumbbell_Press",
    name: "Incline Dumbbell Press",
    primary: ["chest"],
    secondary: ["shoulders", "triceps"],
    equipment: "Dumbbells + incline bench",
    difficulty: "Intermediate",
    description: "Press on an incline bench. Emphasises the upper portion of the pectoralis major.",
    cues: [
      "Set bench to ~30°. Steeper angles push work to front delts",
      "Press up and slightly inward, do not clank the bells together",
      "Lower until elbows are level with the torso",
      "Keep wrists stacked over elbows",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "db_pullover",
    dbId: "Bent-Arm_Dumbbell_Pullover",
    name: "Dumbbell Pullover",
    primary: ["chest"],
    secondary: ["back", "triceps"],
    equipment: "Dumbbell + bench",
    difficulty: "Intermediate",
    description: "Old-school movement that stretches the chest and lats through a deep range.",
    cues: [
      "Lie across a bench with shoulders on the pad, hips low",
      "Hold dumbbell with both hands over the chest, slight elbow bend",
      "Lower behind the head until you feel a deep stretch",
      "Pull the dumbbell back over the chest under control",
    ],
    sets: "3 × 10–12 reps",
    rest: "60–90 sec",
  },
  {
    id: "two_db_row",
    dbId: "Bent_Over_Two-Dumbbell_Row",
    name: "Two-Dumbbell Bent-Over Row",
    primary: ["back"],
    secondary: ["biceps", "forearms", "traps"],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Bilateral bent-over row with dumbbells. Hits the mid-back hard.",
    cues: [
      "Hinge to ~45°, flat back, soft knees",
      "Row both dumbbells to the hips, leading with the elbows",
      "Squeeze the shoulder blades together at the top",
      "Lower under full control — no torso swing",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "reverse_fly",
    dbId: "Reverse_Flyes",
    name: "Reverse Fly",
    primary: ["shoulders"],
    secondary: ["back", "traps"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description:
      "Rear delt isolation. Crucial for posture and shoulder balance — most people are weak here.",
    cues: [
      "Hinge forward ~45°, dumbbells hanging below shoulders",
      "Soft bend in the elbows, lock that angle for the set",
      "Raise the dumbbells out to the sides until at shoulder height",
      "Lead with the elbows; squeeze rear delts at the top",
    ],
    sets: "3 × 12–15 reps",
    rest: "45–60 sec",
  },
  {
    id: "arnold_press",
    dbId: "Arnold_Dumbbell_Press",
    name: "Arnold Press",
    primary: ["shoulders"],
    secondary: ["triceps", "traps"],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Rotating shoulder press. Hits all three heads of the deltoid in one movement.",
    cues: [
      "Start with palms facing you at shoulder level (curl position)",
      "Press up while rotating palms outward",
      "Finish with palms forward, arms extended overhead",
      "Reverse the rotation on the way down — same path, slower",
    ],
    sets: "4 × 8–10 reps",
    rest: "90 sec",
  },
  {
    id: "seated_db_curl",
    dbId: "Seated_Dumbbell_Curl",
    name: "Seated Dumbbell Curl",
    primary: ["biceps"],
    secondary: ["forearms"],
    equipment: "Dumbbells + bench",
    difficulty: "Beginner",
    description: "Seated bicep curl. Removes lower-body momentum and forces strict form.",
    cues: [
      "Sit upright on a bench, dumbbells at sides, palms forward",
      "Curl without swinging the torso",
      "Keep elbows pinned against the sides of your body",
      "Squeeze at the top, lower for 2–3 seconds",
    ],
    sets: "3 × 8–12 reps",
    rest: "60 sec",
  },
  {
    id: "incline_db_curl",
    dbId: "Incline_Dumbbell_Curl",
    name: "Incline Dumbbell Curl",
    primary: ["biceps"],
    secondary: ["forearms"],
    equipment: "Dumbbells + incline bench",
    difficulty: "Intermediate",
    description:
      "Curl with the bench reclined ~45°. Puts the biceps under a deep stretch at the bottom.",
    cues: [
      "Set bench to ~45°, sit back fully",
      "Let arms hang straight down — that stretch is the point",
      "Curl without lifting elbows forward",
      "Squeeze hard at the top; control the descent",
    ],
    sets: "3 × 10–12 reps",
    rest: "60–90 sec",
  },
  {
    id: "zottman_curl",
    dbId: "Zottman_Curl",
    name: "Zottman Curl",
    primary: ["biceps"],
    secondary: ["forearms"],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description:
      "Curl up palms-up, rotate at the top, lower palms-down. Trains biceps and forearm flexors/extensors.",
    cues: [
      "Curl up normally with palms facing you",
      "At the top, rotate so the palms face down",
      "Lower slowly with palms down (this trains the forearms)",
      "Rotate back at the bottom for the next rep",
    ],
    sets: "3 × 8–10 reps",
    rest: "60–90 sec",
  },
  {
    id: "skullcrusher_db",
    dbId: "Lying_Dumbbell_Tricep_Extension",
    name: "Lying Dumbbell Tricep Extension",
    primary: ["triceps"],
    secondary: [],
    equipment: "Dumbbells + bench",
    difficulty: "Intermediate",
    description: "Skull-crusher with dumbbells. Stretches the long head of the triceps.",
    cues: [
      "Lie flat, dumbbells extended above the chest",
      "Bend only at the elbows — upper arms stay vertical",
      "Lower toward the forehead (hence the nickname)",
      "Extend back up, squeeze the triceps at lockout",
    ],
    sets: "3 × 10–12 reps",
    rest: "60–90 sec",
  },
  {
    id: "db_floor_press",
    dbId: "Dumbbell_Floor_Press",
    name: "Dumbbell Floor Press",
    primary: ["chest"],
    secondary: ["triceps", "shoulders"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description:
      "Press from the floor. Shorter range than a bench press — easier on the shoulders.",
    cues: [
      "Lie on the floor, knees bent, dumbbells at chest level",
      "Press up until arms are extended",
      "Lower until upper arms touch the floor — that's the stopping point",
      "Pause for 1 second on the floor; do not bounce",
    ],
    sets: "4 × 6–10 reps",
    rest: "90 sec",
  },
  {
    id: "stiff_leg_db_dl",
    dbId: "Stiff-Legged_Dumbbell_Deadlift",
    name: "Stiff-Legged Dumbbell Deadlift",
    primary: ["hamstrings", "glutes"],
    secondary: ["erectors", "back"],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description:
      "Hip-hinge with mostly straight legs. The single best dumbbell movement for hamstring development.",
    cues: [
      "Soft bend in the knees, hold that angle for the whole rep",
      "Hinge at the hips — push your butt back, not down",
      "Lower until you feel a deep hamstring stretch",
      "Drive the hips forward to stand up; squeeze the glutes at top",
    ],
    sets: "3 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "plie_db_squat",
    dbId: "Plie_Dumbbell_Squat",
    name: "Plie Dumbbell Squat",
    primary: ["quads", "glutes"],
    secondary: ["hamstrings"],
    equipment: "Dumbbell",
    difficulty: "Beginner",
    description:
      "Wide-stance squat with one dumbbell held between the legs. Emphasises inner thighs and glutes.",
    cues: [
      "Stand with feet wider than shoulders, toes pointing out 45°",
      "Hold one dumbbell vertically with both hands between the legs",
      "Squat straight down — knees track over toes",
      "Drive up through the heels; squeeze glutes at top",
    ],
    sets: "3 × 12–15 reps",
    rest: "60–90 sec",
  },
  {
    id: "db_step_up",
    dbId: "Dumbbell_Step_Ups",
    name: "Dumbbell Step-Up",
    primary: ["quads", "glutes"],
    secondary: ["hamstrings", "calves"],
    equipment: "Dumbbells + bench/box",
    difficulty: "Beginner",
    description: "Unilateral leg exercise. Trains single-leg strength and balance.",
    cues: [
      "Stand in front of a bench or sturdy box (knee-height)",
      "Step up with one foot; drive through the heel",
      "Stand tall at the top, then lower under control",
      "Alternate or finish all reps on one side first",
    ],
    sets: "3 × 8–10 reps per leg",
    rest: "60–90 sec",
  },
  {
    id: "standing_db_calf_raise",
    dbId: "Standing_Dumbbell_Calf_Raise",
    name: "Standing Dumbbell Calf Raise",
    primary: ["calves"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description:
      "Calf isolation while holding dumbbells. Hits the gastrocnemius best with straight knees.",
    cues: [
      "Stand on the balls of your feet on the edge of a step",
      "Hold dumbbells at your sides",
      "Lower heels for a stretch, then drive up onto tip-toes",
      "Squeeze the calves hard at the top for 1 second",
    ],
    sets: "4 × 12–20 reps",
    rest: "45–60 sec",
  },
  {
    id: "leg_press",
    dbId: "Leg_Press",
    name: "Leg Press",
    primary: ["quads"],
    secondary: ["glutes", "hamstrings", "calves"],
    equipment: "Leg press machine",
    difficulty: "Beginner",
    description:
      "Machine compound leg press. Loads the quads heavily with the spine supported — a safe way to push volume.",
    cues: [
      "Feet shoulder-width, mid-platform; keep the whole foot down",
      "Lower until knees reach ~90°, hips staying on the pad",
      "Never lock the knees hard at the top",
      "Drive through the heels, not the toes",
    ],
    sets: "4 × 8–12 reps",
    rest: "2 min",
  },
  {
    id: "leg_extension",
    dbId: "Leg_Extensions",
    name: "Leg Extension",
    primary: ["quads"],
    secondary: [],
    equipment: "Leg extension machine",
    difficulty: "Beginner",
    description:
      "Seated quad isolation. Isolates the quadriceps with no help from other muscles — ideal for a finisher.",
    cues: [
      "Align the knee joint with the machine pivot",
      "Extend to nearly straight, pausing at the top",
      "Squeeze the quads hard for 1 second",
      "Lower under control — do not let the stack drop",
    ],
    sets: "3 × 12–15 reps",
    rest: "60 sec",
  },
  {
    id: "lat_pulldown",
    dbId: "Wide-Grip_Lat_Pulldown",
    name: "Lat Pulldown",
    primary: ["back"],
    secondary: ["biceps", "shoulders"],
    equipment: "Cable machine",
    difficulty: "Beginner",
    description:
      "Vertical cable pull for lat width. The pull-up's approachable cousin — scale the load to any level.",
    cues: [
      "Grip wider than shoulders, thumbs over the bar",
      "Pull the bar to the upper chest, driving elbows down",
      "Keep a slight lean back; do not swing the torso",
      "Control the bar back up for a full stretch",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "seated_cable_row",
    dbId: "Seated_Cable_Rows",
    name: "Seated Cable Row",
    primary: ["back"],
    secondary: ["biceps", "shoulders"],
    equipment: "Cable machine",
    difficulty: "Beginner",
    description:
      "Horizontal cable pull for mid-back thickness. Constant tension across the whole range.",
    cues: [
      "Sit tall with a slight knee bend, chest up",
      "Pull the handle to the belly, squeezing shoulder blades",
      "Keep the torso still — no big back-and-forth rocking",
      "Extend the arms fully to stretch the lats",
    ],
    sets: "4 × 10–12 reps",
    rest: "90 sec",
  },
  {
    id: "face_pull",
    dbId: "Face_Pull",
    name: "Face Pull",
    primary: ["shoulders"],
    secondary: ["traps"],
    equipment: "Cable machine",
    difficulty: "Intermediate",
    description:
      "High cable pull to the face. Builds rear delts and shoulder health — great counter to lots of pressing.",
    cues: [
      "Set the pulley at head height with a rope",
      "Pull toward your forehead, hands splitting apart",
      "Lead with the elbows high and wide",
      "Squeeze the rear delts, then return slowly",
    ],
    sets: "3 × 15–20 reps",
    rest: "60 sec",
  },
  {
    id: "barbell_curl",
    dbId: "Barbell_Curl",
    name: "Barbell Curl",
    primary: ["biceps"],
    secondary: ["forearms"],
    equipment: "Barbell",
    difficulty: "Beginner",
    description:
      "Standing barbell biceps curl. Lets you load both arms heavier than dumbbells for raw bicep mass.",
    cues: [
      "Grip shoulder-width, elbows pinned to your sides",
      "Curl with the biceps — do not swing the hips",
      "Squeeze at the top without letting elbows drift forward",
      "Lower for a full 2-second negative",
    ],
    sets: "4 × 8–12 reps",
    rest: "60–90 sec",
  },
  {
    id: "preacher_curl",
    dbId: "Preacher_Curl",
    name: "Preacher Curl",
    primary: ["biceps"],
    secondary: ["forearms"],
    equipment: "Preacher bench + barbell",
    difficulty: "Beginner",
    description:
      "Biceps curl braced on a preacher bench. Kills momentum and hammers the lower biceps.",
    cues: [
      "Rest the back of the arms flat on the pad",
      "Curl the bar up without lifting the elbows",
      "Do not fully lock out at the bottom under load",
      "Lower slowly — the stretched position is where strains happen",
    ],
    sets: "3 × 10–12 reps",
    rest: "60 sec",
  },
  {
    id: "front_squat",
    dbId: "Front_Squat_Clean_Grip",
    name: "Front Squat",
    primary: ["quads"],
    secondary: ["glutes", "hamstrings", "abs"],
    equipment: "Barbell",
    difficulty: "Intermediate",
    description:
      "Barbell squat with the load racked on the front delts. Emphasizes the quads and an upright torso.",
    cues: [
      "Rack the bar on the front delts, elbows high",
      "Keep the chest tall through the whole rep",
      "Sit straight down to at least parallel",
      "Drive up without letting the elbows drop",
    ],
    sets: "4 × 5–8 reps",
    rest: "2–3 min",
  },
  {
    id: "romanian_deadlift",
    dbId: "Romanian_Deadlift",
    name: "Romanian Deadlift",
    primary: ["hamstrings"],
    secondary: ["glutes", "erectors", "back"],
    equipment: "Barbell",
    difficulty: "Intermediate",
    description:
      "Hip-hinge with soft knees. The premier hamstring and glute builder; teaches a strong hinge pattern.",
    cues: [
      "Start standing, bar against the thighs",
      "Push the hips back, knees only slightly bent",
      "Lower the bar along the legs to mid-shin",
      "Feel the hamstring stretch, then drive the hips forward",
    ],
    sets: "4 × 8–10 reps",
    rest: "2 min",
  },
  {
    id: "good_morning",
    dbId: "Good_Morning",
    name: "Good Morning",
    primary: ["hamstrings", "erectors"],
    secondary: ["glutes", "back", "abs"],
    equipment: "Barbell",
    difficulty: "Intermediate",
    description:
      "Bar-on-back hip hinge. Strengthens the posterior chain and bracing — start light, it is humbling.",
    cues: [
      "Bar on the upper back as in a squat",
      "Soft knees; push the hips straight back",
      "Hinge until the torso is near parallel",
      "Keep a flat back the entire way — never round",
    ],
    sets: "3 × 8–10 reps",
    rest: "2 min",
  },
  {
    id: "hanging_leg_raise",
    dbId: "Hanging_Leg_Raise",
    name: "Hanging Leg Raise",
    primary: ["abs"],
    secondary: ["obliques", "forearms"],
    equipment: "Pull-up bar",
    difficulty: "Advanced",
    description:
      "Hanging lower-ab raise. One of the hardest bodyweight core moves — brutal on the lower abs.",
    cues: [
      "Hang from the bar with shoulders engaged",
      "Raise the legs with the abs, not a leg swing",
      "Curl the pelvis up at the top for full contraction",
      "Lower slowly; stop any swinging before the next rep",
    ],
    sets: "3 × 8–15 reps",
    rest: "60–90 sec",
  },
  {
    id: "hack_squat",
    dbId: "Hack_Squat",
    name: "Hack Squat",
    primary: ["quads"],
    secondary: ["glutes", "hamstrings", "calves"],
    equipment: "Hack squat machine",
    difficulty: "Beginner",
    description: "Machine squat on a fixed track. Loads the quads heavily with the back supported.",
    cues: [
      "Shoulders and back flat against the pad",
      "Feet shoulder-width on the platform",
      "Lower to at least parallel, knees tracking the toes",
      "Drive through the heels; avoid locking the knees hard",
    ],
    sets: "4 × 8–12 reps",
    rest: "2 min",
  },
  {
    id: "chin_up",
    dbId: "Chin-Up",
    name: "Chin-Up",
    primary: ["back"],
    secondary: ["biceps", "forearms"],
    equipment: "Pull-up bar",
    difficulty: "Intermediate",
    description:
      "Underhand-grip vertical pull. Hits the lats and biceps hard — often easier than a pull-up.",
    cues: [
      "Grip shoulder-width, palms facing you",
      "Start from a full dead hang",
      "Pull the chin over the bar, driving elbows down",
      "Lower under control for a full stretch",
    ],
    sets: "4 × 5–10 reps (use band assistance if needed)",
    rest: "2 min",
  },
  {
    id: "cable_crossover",
    dbId: "Cable_Crossover",
    name: "Cable Crossover",
    primary: ["chest"],
    secondary: ["shoulders"],
    equipment: "Cable machine",
    difficulty: "Beginner",
    description:
      "Standing cable chest fly. Constant tension and a big stretch make it a superb chest isolator.",
    cues: [
      "Set the pulleys high, take a staggered stance",
      "Slight elbow bend held throughout",
      "Bring the handles together in front, low and forward",
      "Squeeze the chest, then open slowly for the stretch",
    ],
    sets: "3 × 12–15 reps",
    rest: "60 sec",
  },
  {
    id: "triceps_pushdown",
    dbId: "Triceps_Pushdown",
    name: "Triceps Pushdown",
    primary: ["triceps"],
    secondary: [],
    equipment: "Cable machine",
    difficulty: "Beginner",
    description:
      "Cable triceps isolation. Constant tension on the triceps with a joint-friendly setup.",
    cues: [
      "Elbows pinned to your sides the whole set",
      "Push down to full lockout, squeezing the triceps",
      "Keep the torso upright — do not lean and press",
      "Control the bar back up to ~90° at the elbow",
    ],
    sets: "3 × 12–15 reps",
    rest: "60 sec",
  },
  {
    id: "upright_row",
    dbId: "Upright_Barbell_Row",
    name: "Upright Barbell Row",
    primary: ["shoulders"],
    secondary: ["traps", "biceps"],
    equipment: "Barbell",
    difficulty: "Beginner",
    description: "Vertical barbell pull to the collarbone. Builds side delts and traps together.",
    cues: [
      "Grip slightly narrower than shoulder width",
      "Pull the bar up the body, leading with the elbows",
      "Stop when the upper arms reach parallel",
      "Lower slowly; ease off if the shoulders pinch",
    ],
    sets: "3 × 10–12 reps",
    rest: "60–90 sec",
  },
  {
    id: "chest_dip",
    dbId: "Dips_-_Chest_Version",
    name: "Chest Dip",
    primary: ["chest"],
    secondary: ["triceps", "shoulders"],
    equipment: "Dip bars",
    difficulty: "Intermediate",
    description:
      "Bodyweight dip leaned forward to bias the chest. Builds the lower chest and pressing power.",
    cues: [
      "Lean the torso forward, chin tucked",
      "Lower until the shoulders are just below the elbows",
      "Flare the elbows slightly outward, not tucked",
      "Press up and squeeze the chest — stop before locking hard",
    ],
    sets: "4 × 6–12 reps",
    rest: "2 min",
  },
  {
    id: "close_grip_bench",
    dbId: "Close-Grip_Barbell_Bench_Press",
    name: "Close-Grip Bench Press",
    primary: ["triceps"],
    secondary: ["chest", "shoulders"],
    equipment: "Barbell + bench",
    difficulty: "Beginner",
    description:
      "Narrow-grip bench press. Shifts the load to the triceps while still training the chest.",
    cues: [
      "Grip about shoulder-width, not narrower",
      "Keep elbows tucked close to the torso",
      "Lower to the lower chest, wrists stacked",
      "Drive up, keeping the bar path over the elbows",
    ],
    sets: "4 × 6–10 reps",
    rest: "2 min",
  },
  {
    id: "wide_grip_bench",
    dbId: "Wide-Grip_Barbell_Bench_Press",
    name: "Wide-Grip Bench Press",
    primary: ["chest"],
    secondary: ["shoulders", "triceps"],
    equipment: "Barbell + bench",
    difficulty: "Intermediate",
    description:
      "Bench press with a wider grip. Shortens the range and emphasizes the outer chest.",
    cues: [
      "Grip wider than standard, wrists stacked",
      "Retract the shoulder blades hard",
      "Lower to the mid-chest with control",
      "Do not bounce the bar off the chest",
    ],
    sets: "4 × 6–10 reps",
    rest: "2 min",
  },
  {
    id: "machine_shoulder_press",
    dbId: "Machine_Shoulder_Military_Press",
    name: "Machine Shoulder Press",
    primary: ["shoulders"],
    secondary: ["triceps"],
    equipment: "Shoulder press machine",
    difficulty: "Beginner",
    description:
      "Seated machine overhead press. A stable, back-supported way to overload the delts.",
    cues: [
      "Set the seat so handles sit at shoulder height",
      "Press straight up without locking out hard",
      "Keep the lower back against the pad",
      "Lower under control to a full stretch",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "push_press",
    dbId: "Push_Press",
    name: "Push Press",
    primary: ["shoulders"],
    secondary: ["triceps", "quads"],
    equipment: "Barbell",
    difficulty: "Advanced",
    description:
      "Overhead press driven by a leg dip. The leg drive lets you move more weight than a strict press.",
    cues: [
      "Rack the bar on the front delts",
      "Dip a few inches at the knees, torso upright",
      "Explode up, driving the bar overhead",
      "Lock out with the bar over the mid-foot",
    ],
    sets: "4 × 3–6 reps",
    rest: "2–3 min",
  },
  {
    id: "barbell_back_squat",
    dbId: "Barbell_Full_Squat",
    name: "Barbell Back Squat",
    primary: ["quads"],
    secondary: ["glutes", "hamstrings", "erectors"],
    equipment: "Barbell",
    difficulty: "Intermediate",
    description:
      "The king of leg lifts. A deep barbell squat trains the whole lower body and core bracing.",
    cues: [
      "Bar on the upper traps, hands snug",
      "Brace the core, break at the hips and knees together",
      "Descend to at least parallel, knees tracking toes",
      "Drive up through the whole foot, chest tall",
    ],
    sets: "5 × 5 reps",
    rest: "3 min",
  },
  {
    id: "barbell_lunge",
    dbId: "Barbell_Lunge",
    name: "Barbell Lunge",
    primary: ["quads"],
    secondary: ["glutes", "hamstrings", "calves"],
    equipment: "Barbell",
    difficulty: "Intermediate",
    description:
      "Loaded walking or stationary lunge. Builds single-leg strength and balance under a bar.",
    cues: [
      "Bar on the upper back as in a squat",
      "Step out and lower the back knee toward the floor",
      "Keep the front shin near vertical",
      "Push through the front heel to stand",
    ],
    sets: "3 × 8–10 per leg",
    rest: "2 min",
  },
  {
    id: "bulgarian_split_squat",
    dbId: "Split_Squat_with_Dumbbells",
    name: "Bulgarian Split Squat",
    primary: ["quads"],
    secondary: ["glutes", "hamstrings"],
    equipment: "Dumbbells + bench",
    difficulty: "Intermediate",
    description:
      "Rear-foot-elevated split squat. A brutal single-leg builder for the quads and glutes.",
    cues: [
      "Rest the rear foot on a bench behind you",
      "Front foot far enough forward for a vertical shin",
      "Lower straight down until the front thigh is parallel",
      "Drive up through the front heel",
    ],
    sets: "3 × 8–12 per leg",
    rest: "90 sec",
  },
  {
    id: "sumo_deadlift",
    dbId: "Sumo_Deadlift",
    name: "Sumo Deadlift",
    primary: ["hamstrings", "glutes"],
    secondary: ["quads", "adductors", "erectors", "back", "traps"],
    equipment: "Barbell",
    difficulty: "Intermediate",
    description:
      "Wide-stance deadlift. Shorter range and more upright torso; loads the hips and quads heavily.",
    cues: [
      "Feet wide, toes pointed out, grip inside the knees",
      "Push the knees out over the toes",
      "Chest up, take the slack out before pulling",
      "Drive the floor away and lock out with the glutes",
    ],
    sets: "4 × 3–6 reps",
    rest: "3 min",
  },
  {
    id: "rack_pull",
    dbId: "Rack_Pulls",
    name: "Rack Pull",
    primary: ["erectors", "traps"],
    secondary: ["glutes", "hamstrings", "back"],
    equipment: "Barbell + rack",
    difficulty: "Intermediate",
    description:
      "Partial deadlift from pins. Overloads the upper back and traps through the top half.",
    cues: [
      "Set the pins at or just below the knee",
      "Grip the bar, brace, and pull to lockout",
      "Keep a flat back — do not round to reach lower",
      "Squeeze the glutes and traps at the top",
    ],
    sets: "4 × 5–8 reps",
    rest: "2–3 min",
  },
  {
    id: "tbar_row",
    dbId: "T-Bar_Row_with_Handle",
    name: "T-Bar Row",
    primary: ["back"],
    secondary: ["biceps", "traps"],
    equipment: "T-bar / landmine",
    difficulty: "Beginner",
    description:
      "Chest-supported-style barbell row on a landmine. Loads the mid-back with a strong, stable line.",
    cues: [
      "Hinge at the hips, flat back, knees soft",
      "Pull the handle to the lower chest",
      "Drive the elbows back and squeeze the shoulder blades",
      "Lower under control to a full stretch",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "seated_leg_curl",
    dbId: "Seated_Leg_Curl",
    name: "Seated Leg Curl",
    primary: ["hamstrings"],
    secondary: [],
    equipment: "Seated leg curl machine",
    difficulty: "Beginner",
    description:
      "Seated machine hamstring curl. Isolates the hamstrings with a strong stretch under load.",
    cues: [
      "Align the knees with the machine pivot",
      "Curl the pad down as far as it goes",
      "Squeeze the hamstrings at the bottom",
      "Return slowly, resisting the stack",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "seated_calf_raise",
    dbId: "Seated_Calf_Raise",
    name: "Seated Calf Raise",
    primary: ["calves"],
    secondary: [],
    equipment: "Seated calf machine",
    difficulty: "Beginner",
    description:
      "Bent-knee calf raise. Targets the soleus, the deeper calf muscle straight knees miss.",
    cues: [
      "Pad across the lower thighs, balls of feet on the plate",
      "Drop the heels for a full stretch",
      "Drive up onto the toes as high as possible",
      "Pause and squeeze at the top",
    ],
    sets: "4 × 12–20 reps",
    rest: "45–60 sec",
  },
  {
    id: "cable_hip_adduction",
    dbId: "Cable_Hip_Adduction",
    name: "Cable Hip Adduction",
    primary: ["adductors"],
    secondary: [],
    equipment: "Cable machine",
    difficulty: "Beginner",
    description:
      "Standing cable pull of the leg across the body. Trains the inner-thigh adductors.",
    cues: [
      "Cuff the ankle nearest the machine",
      "Stand tall, brace the core",
      "Pull the working leg across in front of the other",
      "Return slowly against the resistance",
    ],
    sets: "3 × 12–15 per leg",
    rest: "45 sec",
  },
  {
    id: "cable_crunch",
    dbId: "Cable_Crunch",
    name: "Cable Crunch",
    primary: ["abs"],
    secondary: [],
    equipment: "Cable machine",
    difficulty: "Beginner",
    description: "Kneeling weighted crunch on a cable. Lets you progressively overload the abs.",
    cues: [
      "Kneel below a high rope, hold it by your head",
      "Crunch down by rounding the spine, not bending the hips",
      "Bring the elbows toward the thighs",
      "Return slowly, keeping tension on the abs",
    ],
    sets: "3 × 12–15 reps",
    rest: "60 sec",
  },
  {
    id: "ab_roller",
    dbId: "Ab_Roller",
    name: "Ab Wheel Rollout",
    primary: ["abs"],
    secondary: ["shoulders", "back"],
    equipment: "Ab wheel",
    difficulty: "Intermediate",
    description:
      "Rollout on an ab wheel. Demands huge anti-extension core strength — start from the knees.",
    cues: [
      "Kneel, grip the wheel under the shoulders",
      "Brace hard and roll out as far as you control",
      "Keep the hips from sagging — no arching the low back",
      "Pull back with the abs, not the arms",
    ],
    sets: "3 × 8–12 reps",
    rest: "60–90 sec",
  },
  {
    id: "side_plank",
    dbId: "Side_Bridge",
    name: "Side Plank",
    primary: ["obliques"],
    secondary: ["abs", "shoulders"],
    equipment: "Bodyweight",
    difficulty: "Beginner",
    description: "Isometric side hold. Trains the obliques and lateral core stability.",
    cues: [
      "Stack the feet, elbow under the shoulder",
      "Lift the hips into a straight line",
      "Do not let the hips sag toward the floor",
      "Hold, breathing steadily; switch sides",
    ],
    sets: "3 × 30–45 sec per side",
    rest: "30 sec",
  },
  {
    id: "mountain_climbers",
    dbId: "Mountain_Climbers",
    name: "Mountain Climbers",
    primary: ["abs"],
    secondary: ["shoulders", "quads"],
    equipment: "Bodyweight",
    difficulty: "Beginner",
    description: "Dynamic plank with driving knees. A core and conditioning move in one.",
    cues: [
      "Start in a high plank, hands under shoulders",
      "Drive one knee toward the chest, then switch",
      "Keep the hips low and level — no bouncing up",
      "Move fast but keep the plank line",
    ],
    sets: "3 × 30–40 sec",
    rest: "30–45 sec",
  },
  {
    id: "standing_db_tri_ext",
    dbId: "Standing_Dumbbell_Triceps_Extension",
    name: "Standing Dumbbell Triceps Extension",
    primary: ["triceps"],
    secondary: [],
    equipment: "Dumbbell",
    difficulty: "Beginner",
    description:
      "Overhead dumbbell triceps extension. Hits the long head of the triceps through a big stretch.",
    cues: [
      "Hold one dumbbell overhead with both hands",
      "Keep the elbows in and pointing up",
      "Lower behind the head for a full stretch",
      "Extend to lockout, squeezing the triceps",
    ],
    sets: "3 × 10–12 reps",
    rest: "60 sec",
  },
  {
    id: "ez_bar_curl",
    dbId: "EZ-Bar_Curl",
    name: "EZ-Bar Curl",
    primary: ["biceps"],
    secondary: ["forearms"],
    equipment: "EZ curl bar",
    difficulty: "Beginner",
    description:
      "Biceps curl on an angled bar. Easier on the wrists than a straight bar while loading both arms.",
    cues: [
      "Grip the inner angles, elbows at your sides",
      "Curl up without swinging the torso",
      "Squeeze at the top, elbows staying put",
      "Lower for a full 2-second negative",
    ],
    sets: "4 × 8–12 reps",
    rest: "60–90 sec",
  },
  {
    id: "alternate_hammer_curl",
    dbId: "Alternate_Hammer_Curl",
    name: "Alternate Hammer Curl",
    primary: ["biceps"],
    secondary: ["forearms"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the biceps.",
    cues: [
      "Stand up with your torso upright and a dumbbell in each hand being held at arms length.",
      "The palms of the hands should be facing your torso.",
      "While holding the upper arm stationary, curl the right weight forward while contracting the bicep...",
      "Slowly begin to bring the dumbbells back to starting position as your breathe in.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "alternate_incline_dumbbell_curl",
    dbId: "Alternate_Incline_Dumbbell_Curl",
    name: "Alternate Incline Dumbbell Curl",
    primary: ["biceps"],
    secondary: ["forearms"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the biceps.",
    cues: [
      "Sit down on an incline bench with a dumbbell in each hand being held at arms length.",
      "While holding the upper arm stationary, curl the right weight forward while contracting the bicep...",
      "Slowly begin to bring the dumbbell back to starting position as your breathe in.",
      "Repeat the movement with the left hand.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "alternating_deltoid_raise",
    dbId: "Alternating_Deltoid_Raise",
    name: "Alternating Deltoid Raise",
    primary: ["shoulders"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the shoulders.",
    cues: [
      "In a standing position, hold a pair of dumbbells at your side.",
      "Keeping your elbows slightly bent, raise the weights directly in front of you to shoulder height,...",
      "Return the weights to your side.",
      "On the next repetition, raise the weights laterally, raising them out to your side to about shoul...",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "around_the_worlds",
    dbId: "Around_The_Worlds",
    name: "Around The Worlds",
    primary: ["chest"],
    secondary: ["shoulders"],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell compound movement targeting the chest.",
    cues: [
      "Lay down on a flat bench holding a dumbbell in each hand with the palms of the hands facing towar...",
      "Now move the dumbbells by creating a semi-circle as you displace them from the initial position t...",
      "Reverse the movement to return the weight to the starting position as you exhale.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "bent_over_dumbbell_rear_delt_raise_with_head_on_bench",
    dbId: "Bent_Over_Dumbbell_Rear_Delt_Raise_With_Head_On_Bench",
    name: "Bent Over Dumbbell Rear Delt Raise With Head On Bench",
    primary: ["shoulders"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the shoulders.",
    cues: [
      "Stand up straight while holding a dumbbell in each hand and with an incline bench in front of you.",
      "While keeping your back straight and maintaining the natural arch of your back, lean forward unti...",
      "Keeping your torso forward and stationary, and the arms straight with a slight bend at the elbows...",
      "After a one second contraction at the top, slowly lower the dumbbells back to the starting position.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "bent_over_two_dumbbell_row_with_palms_in",
    dbId: "Bent_Over_Two-Dumbbell_Row_With_Palms_In",
    name: "Bent Over Two-Dumbbell Row With Palms In",
    primary: ["back"],
    secondary: ["biceps"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell compound movement targeting the back.",
    cues: [
      "With a dumbbell in each hand (palms facing each other), bend your knees slightly and bring your t...",
      "While keeping the torso stationary, lift the dumbbells to your side as you breathe out, squeezing...",
      "Slowly lower the weight again to the starting position as you inhale.",
      "Repeat for the recommended amount of repetitions.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "calf_raise_on_a_dumbbell",
    dbId: "Calf_Raise_On_A_Dumbbell",
    name: "Calf Raise On A Dumbbell",
    primary: ["calves"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell isolation movement targeting the calves.",
    cues: [
      "Hang on to a sturdy object for balance and stand on a dumbbell handle, preferably one with round...",
      "Now roll your foot slightly forward so that you can get a nice stretch of the calf.",
      "Lift the calf as you roll your foot over the top of the handle so that you get a full extension.",
      "Now inhale as you roll the dumbbell slightly forward as you come down to get a better stretch.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "close_grip_dumbbell_press",
    dbId: "Close-Grip_Dumbbell_Press",
    name: "Close-Grip Dumbbell Press",
    primary: ["triceps"],
    secondary: ["chest", "shoulders"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell compound movement targeting the triceps.",
    cues: [
      "Place a dumbbell standing up on a flat bench.",
      "Ensuring that the dumbbell stays securely placed at the top of the bench, lie perpendicular to th...",
      "Grasp the dumbbell with both hands and hold it straight over your chest at arm's length.",
      "Initiate the movement by lowering the dumbbell to your chest.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "cross_body_hammer_curl",
    dbId: "Cross_Body_Hammer_Curl",
    name: "Cross Body Hammer Curl",
    primary: ["biceps"],
    secondary: ["forearms"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the biceps.",
    cues: [
      "Stand up straight with a dumbbell in each hand.",
      "While keeping your palms facing in and without twisting your arm, curl the dumbbell of the right...",
      "Slowly lower the dumbbell along the same path as you inhale and then repeat the same movement for...",
      "Continue alternating in this fashion until the recommended amount of repetitions is performed for...",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "cuban_press",
    dbId: "Cuban_Press",
    name: "Cuban Press",
    primary: ["shoulders"],
    secondary: ["traps"],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell compound movement targeting the shoulders.",
    cues: [
      "Take a dumbbell in each hand with a pronated grip in a standing position.",
      "To initiate the movement, externally rotate the shoulders to move the upper arm 180 degrees.",
      "Now press the dumbbells by extending at the elbows, straightening your arms overhead.",
      "Return to the starting position as you breathe in by reversing the steps.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "decline_dumbbell_bench_press",
    dbId: "Decline_Dumbbell_Bench_Press",
    name: "Decline Dumbbell Bench Press",
    primary: ["chest"],
    secondary: ["shoulders", "triceps"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell compound movement targeting the chest.",
    cues: [
      "Secure your legs at the end of the decline bench and lie down with a dumbbell on each hand on top...",
      "Once you are laying down, move the dumbbells in front of you at shoulder width.",
      "Once at shoulder width, rotate your wrists forward so that the palms of your hands are facing awa...",
      "Bring down the weights slowly to your side as you breathe out.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "decline_dumbbell_flyes",
    dbId: "Decline_Dumbbell_Flyes",
    name: "Decline Dumbbell Flyes",
    primary: ["chest"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell compound movement targeting the chest.",
    cues: [
      "Secure your legs at the end of the decline bench and lie down with a dumbbell on each hand on top...",
      "Once you are laying down, move the dumbbells in front of you at shoulder width.",
      "With a slight bend on your elbows in order to prevent stress at the biceps tendon, lower your arm...",
      "Return your arms back to the starting position as you squeeze your chest muscles and breathe out.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "decline_dumbbell_triceps_extension",
    dbId: "Decline_Dumbbell_Triceps_Extension",
    name: "Decline Dumbbell Triceps Extension",
    primary: ["triceps"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the triceps.",
    cues: [
      "Secure your legs at the end of the decline bench and lie down with a dumbbell on each hand on top...",
      "Once you are laying down, move the dumbbells in front of you at shoulder width.",
      "As you breathe in and you keep the upper arms stationary (and elbows in), bring the dumbbells dow...",
      "Lift the dumbbells back to the starting position by contracting the triceps and exhaling.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "dumbbell_bench_press_with_neutral_grip",
    dbId: "Dumbbell_Bench_Press_with_Neutral_Grip",
    name: "Dumbbell Bench Press with Neutral Grip",
    primary: ["chest"],
    secondary: ["shoulders", "triceps"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell compound movement targeting the chest.",
    cues: [
      "Take a dumbbell in each hand and lay back onto a flat bench.",
      "Maintaining a neutral grip, palms facing each other, begin with your arms extended directly above...",
      "Begin the movement by flexing the elbow, lowering the upper arms to the side.",
      "Pause, then extend the elbow and return to the starting position.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "dumbbell_bicep_curl",
    dbId: "Dumbbell_Bicep_Curl",
    name: "Dumbbell Bicep Curl",
    primary: ["biceps"],
    secondary: ["forearms"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the biceps.",
    cues: [
      "Stand up straight with a dumbbell in each hand at arm's length.",
      "Now, keeping the upper arms stationary, exhale and curl the weights while contracting your biceps.",
      "Then, inhale and slowly begin to lower the dumbbells back to the starting position.",
      "Repeat for the recommended amount of repetitions.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "dumbbell_clean",
    dbId: "Dumbbell_Clean",
    name: "Dumbbell Clean",
    primary: ["hamstrings"],
    secondary: ["calves", "forearms", "glutes", "back", "quads", "shoulders", "traps"],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell compound movement targeting the hamstrings.",
    cues: [
      "Begin standing with a dumbbell in each hand with your feet shoulder width apart.",
      "Lower the weights to the floor by flexing at the hips and knees, pushing your hips back until the...",
      "To initiate the movement, violently jump upward by extending the hips, knees, and ankles to accle...",
      "After full extension, rebend the hips and knees to receive the weight in a squat position.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "dumbbell_incline_row",
    dbId: "Dumbbell_Incline_Row",
    name: "Dumbbell Incline Row",
    primary: ["back"],
    secondary: ["biceps", "forearms", "shoulders"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell compound movement targeting the back.",
    cues: [
      "Using a neutral grip, lean into an incline bench.",
      "Take a dumbbell in each hand with a neutral grip, beginning with the arms straight.",
      "Retract the shoulder blades and flex the elbows to row the dumbbells to your side.",
      "Pause at the top of the motion, and then return to the starting position.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "dumbbell_incline_shoulder_raise",
    dbId: "Dumbbell_Incline_Shoulder_Raise",
    name: "Dumbbell Incline Shoulder Raise",
    primary: ["shoulders"],
    secondary: ["triceps"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the shoulders.",
    cues: [
      "Sit on an Incline Bench while holding a dumbbell on each hand on top of your thighs.",
      "Lift your legs up to kick the weights to your shoulders and lean back.",
      "While keeping the arms straight and locked, lift the dumbbells by raising the shoulders from the...",
      "Bring back the dumbbells to the starting position as you breathe in.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "dumbbell_lying_one_arm_rear_lateral_raise",
    dbId: "Dumbbell_Lying_One-Arm_Rear_Lateral_Raise",
    name: "Dumbbell Lying One-Arm Rear Lateral Raise",
    primary: ["shoulders"],
    secondary: ["back"],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell isolation movement targeting the shoulders.",
    cues: [
      "While holding a dumbbell in one hand, lay with your chest down on a slightly inclined (around 15...",
      "Position the palm of the hand that is holding the dumbbell in a neutral manner (palms facing your...",
      "Now raise the arm with the dumbbell to the side until your elbow is at shoulder height and your a...",
      "Slowly lower the dumbbell to the starting position as you inhale.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "dumbbell_lying_pronation",
    dbId: "Dumbbell_Lying_Pronation",
    name: "Dumbbell Lying Pronation",
    primary: ["forearms"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell isolation movement targeting the forearms.",
    cues: [
      "Lie on a flat bench face down with one arm holding a dumbbell and the other hand on top of the be...",
      "Bend the elbows of the arm holding the dumbbell so that it creates a 90-degree angle between the...",
      "Now raise the upper arm so that the forearm is perpendicular to the floor and the upper arm is pe...",
      "As you breathe out, externally rotate your forearm so that the dumbbell is lifted forward as you...",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "dumbbell_lying_rear_lateral_raise",
    dbId: "Dumbbell_Lying_Rear_Lateral_Raise",
    name: "Dumbbell Lying Rear Lateral Raise",
    primary: ["shoulders"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell isolation movement targeting the shoulders.",
    cues: [
      "While holding a dumbbell in each hand, lay with your chest down on a slightly inclined (around 15...",
      "Position the palms of the hands in a neutral manner (palms facing your torso) as you keep the arm...",
      "Now raise the arms to the side until your elbows are at shoulder height and your arms are roughly...",
      "Slowly lower the dumbbells to the starting position as you inhale.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "dumbbell_lying_supination",
    dbId: "Dumbbell_Lying_Supination",
    name: "Dumbbell Lying Supination",
    primary: ["forearms"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell isolation movement targeting the forearms.",
    cues: [
      "Lie sideways on a flat bench with one arm holding a dumbbell and the other hand on top of the ben...",
      "Bend the elbows of the arm holding the dumbbell so that it creates a 90-degree angle between the...",
      "Now raise the upper arm so that the forearm is parallel to the floor and perpendicular to your to...",
      "As you breathe out, externally rotate your forearm so that the dumbbell is lifted up in a semicir...",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "dumbbell_one_arm_shoulder_press",
    dbId: "Dumbbell_One-Arm_Shoulder_Press",
    name: "Dumbbell One-Arm Shoulder Press",
    primary: ["shoulders"],
    secondary: ["triceps"],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell compound movement targeting the shoulders.",
    cues: [
      "Grab a dumbbell and either sit on a military press bench or a utility bench that has a back suppo...",
      "Clean the dumbbell up to bring it to shoulder height.",
      "Rotate the wrist so that the palm of your hand is facing forward.",
      "As you exhale, push the dumbbell up until your arm is fully extended.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "dumbbell_one_arm_triceps_extension",
    dbId: "Dumbbell_One-Arm_Triceps_Extension",
    name: "Dumbbell One-Arm Triceps Extension",
    primary: ["triceps"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell isolation movement targeting the triceps.",
    cues: [
      "Grab a dumbbell and either sit on a military press bench or a utility bench that has a back suppo...",
      "Clean the dumbbell up to bring it to shoulder height and then extend the arm over your head so th...",
      "Rotate the wrist so that the palm of your hand is facing forward and the pinkie is facing the cei...",
      "Slowly lower the dumbbell behind your head as you hold the upper arm stationary.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "dumbbell_one_arm_upright_row",
    dbId: "Dumbbell_One-Arm_Upright_Row",
    name: "Dumbbell One-Arm Upright Row",
    primary: ["shoulders"],
    secondary: ["biceps", "traps"],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell compound movement targeting the shoulders.",
    cues: [
      "Grab a dumbbell and stand up straight with your arm extended in front of you with a slight bend a...",
      "Keep the other hand can be kept fully extended to the side, by the waist or grabbing a fixed surf...",
      "Use your side shoulders to lift the dumbbell as you exhale.",
      "Lower the dumbbell back down slowly to the starting position.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "dumbbell_prone_incline_curl",
    dbId: "Dumbbell_Prone_Incline_Curl",
    name: "Dumbbell Prone Incline Curl",
    primary: ["biceps"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell isolation movement targeting the biceps.",
    cues: [
      "Grab a dumbbell on each hand and lie face down on an incline bench with your shoulders near top o...",
      "Let your arms extend and hang naturally in front of you so that they are perpendicular to the floor.",
      "Now keep your elbows in by your side and face the palms forward.",
      "Raise the dumbbells by contracting the biceps until your arms are fully flexed.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "dumbbell_raise",
    dbId: "Dumbbell_Raise",
    name: "Dumbbell Raise",
    primary: ["shoulders"],
    secondary: ["biceps"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell compound movement targeting the shoulders.",
    cues: [
      "Grab a dumbbell in each arm and stand up straight with your arms extended by your sides with a sl...",
      "Use your side shoulders to lift the dumbbells as you exhale.",
      "Lower the dumbbells back down slowly to the starting position.",
      "Repeat for the recommended amount of repetitions.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "dumbbell_rear_lunge",
    dbId: "Dumbbell_Rear_Lunge",
    name: "Dumbbell Rear Lunge",
    primary: ["quads"],
    secondary: ["calves", "glutes", "hamstrings"],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell compound movement targeting the quads.",
    cues: [
      "Stand with your torso upright holding two dumbbells in your hands by your sides.",
      "Step backward with your right leg around two feet or so from the left foot and lower your upper b...",
      "Push up and go back to the starting position as you exhale.",
      "Now repeat with the opposite leg.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "dumbbell_scaption",
    dbId: "Dumbbell_Scaption",
    name: "Dumbbell Scaption",
    primary: ["shoulders"],
    secondary: ["traps"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the shoulders.",
    cues: [
      "This corrective exercise strengthens the muscles that stabilize your shoulder blade.",
      "Begin the movement raising your arms out in front of you, about 30 degrees off center.",
      "Continue until your arms are parallel to the ground, and then return to the starting position.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "dumbbell_seated_box_jump",
    dbId: "Dumbbell_Seated_Box_Jump",
    name: "Dumbbell Seated Box Jump",
    primary: ["quads"],
    secondary: ["calves", "glutes", "hamstrings"],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell compound movement targeting the quads.",
    cues: [
      "Position a box a couple feet to the side of a bench.",
      "Plant your feet firmly on the ground as you lean forward, extending through the hips and knees to...",
      "Land on the box with both feet, absorbing the impact by allowing the hips and knees to bend.",
      "Step down and return to the starting position.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "dumbbell_seated_one_leg_calf_raise",
    dbId: "Dumbbell_Seated_One-Leg_Calf_Raise",
    name: "Dumbbell Seated One-Leg Calf Raise",
    primary: ["calves"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the calves.",
    cues: [
      "Place a block on the floor about 12 inches from a flat bench.",
      "Sit on a flat bench and place a dumbbell on your upper left thigh about 3 inches above your knee.",
      "Now place the ball of your left foot on the block.",
      "Raise your toes up as high as possible as you exhale and you contract your calf muscle.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "dumbbell_squat_to_a_bench",
    dbId: "Dumbbell_Squat_To_A_Bench",
    name: "Dumbbell Squat To A Bench",
    primary: ["quads"],
    secondary: ["calves", "glutes", "hamstrings", "erectors"],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell compound movement targeting the quads.",
    cues: [
      "Stand up straight with a flat bench behind you while holding a dumbbell on each hand (palms facin...",
      "Position your legs using a shoulder width medium stance with the toes slightly pointed out.",
      "Begin to slowly lower your torso by bending the knees as you maintain a straight posture with the...",
      "Begin to raise the bar as you exhale by pushing the floor with the heel of your foot mainly as yo...",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "dumbbell_tricep_extension_pronated_grip",
    dbId: "Dumbbell_Tricep_Extension_-Pronated_Grip",
    name: "Dumbbell Tricep Extension -Pronated Grip",
    primary: ["triceps"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the triceps.",
    cues: [
      "Lie down on a flat bench holding two dumbbells directly above your shoulders.",
      "The palms of your hands should be facing forward, and your elbows should be tucked in.",
      "Now, inhale and slowly lower the dumbbells until they are near your ears.",
      "Then, exhale and use your triceps to return the weight to the starting position.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "external_rotation",
    dbId: "External_Rotation",
    name: "External Rotation",
    primary: ["shoulders"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the shoulders.",
    cues: [
      "Lie sideways on a flat bench with one arm holding a dumbbell and the other hand on top of the ben...",
      "Bend the elbows of the arm holding the dumbbell so that it creates a 90-degree angle between the...",
      "Now bend the elbow while keeping the upper arm stationary.",
      "As you breathe out, externally rotate your forearm so that the dumbbell is lifted up in a semicir...",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "flexor_incline_dumbbell_curls",
    dbId: "Flexor_Incline_Dumbbell_Curls",
    name: "Flexor Incline Dumbbell Curls",
    primary: ["biceps"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the biceps.",
    cues: [
      "Hold the dumbbell towards the side farther from you so that you have more weight on the side clos...",
      "Sit on an incline bench that is angled at 45-degrees while holding a dumbbell on each hand.",
      "Let your arms hang down on your sides, with the elbows in, and turn the palms of your hands forwa...",
      "Curl up the two dumbbells at the same time until your biceps are fully contracted and exhale.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "front_incline_dumbbell_raise",
    dbId: "Front_Incline_Dumbbell_Raise",
    name: "Front Incline Dumbbell Raise",
    primary: ["shoulders"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the shoulders.",
    cues: [
      "Sit down on an incline bench with the incline set anywhere between 30 to 60 degrees while holding...",
      "Extend your arms straight in front of you and have your palms facing down with the dumbbells rais...",
      "Slowly raise the dumbbells straight up until they are slightly above your shoulders, while keepin...",
      "Lower the arms back to the starting position as you inhale.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "front_two_dumbbell_raise",
    dbId: "Front_Two-Dumbbell_Raise",
    name: "Front Two-Dumbbell Raise",
    primary: ["shoulders"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the shoulders.",
    cues: [
      "Pick a couple of dumbbells and stand with a straight torso and the dumbbells on front of your thi...",
      "While maintaining the torso stationary (no swinging), lift the dumbbells to the front with a slig...",
      "As you inhale, lower the dumbbells back down slowly to the starting position.",
      "Repeat for the recommended amount of repetitions.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "hammer_grip_incline_db_bench_press",
    dbId: "Hammer_Grip_Incline_DB_Bench_Press",
    name: "Hammer Grip Incline DB Bench Press",
    primary: ["chest"],
    secondary: ["shoulders", "triceps"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell compound movement targeting the chest.",
    cues: [
      "Lie back on an incline bench with a dumbbell on each hand on top of your thighs.",
      "By using your thighs to help you get the dumbbells up, clean the dumbbells one arm at a time so t...",
      "Once at shoulder width, keep the palms of your hands with a neutral grip (palms facing each other).",
      "Now bring down the weights slowly to your side as you breathe in.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "incline_dumbbell_bench_with_palms_facing_in",
    dbId: "Incline_Dumbbell_Bench_With_Palms_Facing_In",
    name: "Incline Dumbbell Bench With Palms Facing In",
    primary: ["chest"],
    secondary: ["shoulders", "triceps"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell compound movement targeting the chest.",
    cues: [
      "Lie back on an incline bench with a dumbbell on each hand on top of your thighs.",
      "By using your thighs to help you get the dumbbells up, clean the dumbbells one arm at a time so t...",
      "Once at shoulder width, keep the palms of your hands with a neutral grip (palms facing each other).",
      "Now bring down the weights slowly to your side as you breathe in.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "incline_dumbbell_flyes",
    dbId: "Incline_Dumbbell_Flyes",
    name: "Incline Dumbbell Flyes",
    primary: ["chest"],
    secondary: ["shoulders"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell compound movement targeting the chest.",
    cues: [
      "Hold a dumbbell on each hand and lie on an incline bench that is set to an incline angle of no mo...",
      "Extend your arms above you with a slight bend at the elbows.",
      "Now rotate the wrists so that the palms of your hands are facing you.",
      "As you breathe in, start to slowly lower the arms to the side while keeping the arms extended and...",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "incline_dumbbell_flyes_with_a_twist",
    dbId: "Incline_Dumbbell_Flyes_-_With_A_Twist",
    name: "Incline Dumbbell Flyes - With A Twist",
    primary: ["chest"],
    secondary: ["shoulders"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell compound movement targeting the chest.",
    cues: [
      "Hold a dumbbell in each hand and lie on an incline bench that is set to an incline angle of no mo...",
      "Extend your arms above you with a slight bend at the elbows.",
      "Now rotate the wrists so that the palms of your hands are facing you.",
      "As you breathe in, start to slowly lower the arms to the side while keeping the arms extended and...",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "incline_hammer_curls",
    dbId: "Incline_Hammer_Curls",
    name: "Incline Hammer Curls",
    primary: ["biceps"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the biceps.",
    cues: [
      "Seat yourself on an incline bench with a dumbbell in each hand.",
      "Initiate the movement by flexing at the elbow, attempting to keep the upper arm stationary.",
      "Continue to the top of the movement and pause, then slowly return to the start position.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "incline_inner_biceps_curl",
    dbId: "Incline_Inner_Biceps_Curl",
    name: "Incline Inner Biceps Curl",
    primary: ["biceps"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the biceps.",
    cues: [
      "Hold a dumbbell in each hand and lie back on an incline bench.",
      "The dumbbells should be at arm's length hanging at your sides and your palms should be facing out.",
      "Now as you exhale curl the weight outward and up while keeping your forearms in line with your si...",
      "After a second contraction at the top of the movement, start to inhale and slowly lower the weigh...",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "iron_cross",
    dbId: "Iron_Cross",
    name: "Iron Cross",
    primary: ["shoulders"],
    secondary: ["chest", "glutes", "hamstrings", "back", "quads", "traps"],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell compound movement targeting the shoulders.",
    cues: ["Perform with controlled form through a full range of motion"],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "lying_one_arm_lateral_raise",
    dbId: "Lying_One-Arm_Lateral_Raise",
    name: "Lying One-Arm Lateral Raise",
    primary: ["shoulders"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell isolation movement targeting the shoulders.",
    cues: [
      "While holding a dumbbell in one hand, lay with your chest down on a flat bench.",
      "Position the palm of the hand that is holding the dumbbell in a neutral manner (palms facing your...",
      "Now raise the arm with the dumbbell to the side until your elbow is at shoulder height and your a...",
      "Slowly lower the dumbbell to the starting position as you inhale.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "lying_rear_delt_raise",
    dbId: "Lying_Rear_Delt_Raise",
    name: "Lying Rear Delt Raise",
    primary: ["shoulders"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell isolation movement targeting the shoulders.",
    cues: [
      "While holding a dumbbell in each hand, lay with your chest down on a flat bench.",
      "Position the palms of the hands in a neutral manner (palms facing your torso) as you keep the arm...",
      "Now raise the arms to the side until your elbows are at shoulder height and your arms are roughly...",
      "Slowly lower the dumbbells to the starting position as you inhale.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "lying_supine_dumbbell_curl",
    dbId: "Lying_Supine_Dumbbell_Curl",
    name: "Lying Supine Dumbbell Curl",
    primary: ["biceps"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the biceps.",
    cues: [
      "Lie down on a flat bench face up while holding a dumbbell in each arm on top of your thighs.",
      "Bring the dumbbells to the sides with the arms extended and the palms of the hands facing your th...",
      "While keeping the arms close to your torso and elbows in, slowly lower your arms (as you keep the...",
      "As you breathe out, slowly begin to curl the weights up as you simultaneously rotate your wrists...",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "middle_back_shrug",
    dbId: "Middle_Back_Shrug",
    name: "Middle Back Shrug",
    primary: ["traps"],
    secondary: ["back"],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell isolation movement targeting the back.",
    cues: [
      "Lie facedown on an incline bench while holding a dumbbell in each hand.",
      "As you exhale, squeeze your shoulder blades together and hold the contraction for a full second.",
      "As you inhale go back to the starting position.",
      "Repeat for the recommended amount of repetitions.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "one_arm_flat_bench_dumbbell_flye",
    dbId: "One-Arm_Flat_Bench_Dumbbell_Flye",
    name: "One-Arm Flat Bench Dumbbell Flye",
    primary: ["chest"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the chest.",
    cues: [
      "Lie down on a flat bench with a dumbbell in one hand resting on top of your thigh.",
      "By using your thighs to help you get the dumbbell up, clean the dumbbell so that you can hold it...",
      "Your arm with the weight should have a slight bend on your elbow in order to prevent stress at th...",
      "Return your lifting arm back to the starting position as you squeeze your chest muscles and breat...",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "one_arm_incline_lateral_raise",
    dbId: "One-Arm_Incline_Lateral_Raise",
    name: "One-Arm Incline Lateral Raise",
    primary: ["shoulders"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the shoulders.",
    cues: [
      "Lie down sideways on an incline bench press with a dumbbell in the hand.",
      "Hold a dumbbell in your uppermost arm while keeping it extended in front of you parallel to the f...",
      "While keeping the dumbbell parallel to the floor at all times, perform a lateral raise.",
      "While inhaling lower the weight across your body back into the starting position.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "one_arm_side_laterals",
    dbId: "One-Arm_Side_Laterals",
    name: "One-Arm Side Laterals",
    primary: ["shoulders"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the shoulders.",
    cues: [
      "Pick a dumbbell and place it in one of your hands.",
      "Stand with a straight torso and have the dumbbell by your side at arm's length with the palm of t...",
      "While maintaining the torso stationary (no swinging), lift the dumbbell to your side with a sligh...",
      "Lower the dumbbell back down slowly to the starting position as you inhale.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "one_arm_dumbbell_bench_press",
    dbId: "One_Arm_Dumbbell_Bench_Press",
    name: "One Arm Dumbbell Bench Press",
    primary: ["chest"],
    secondary: ["shoulders", "triceps"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell compound movement targeting the chest.",
    cues: [
      "Lie down on a flat bench with a dumbbell in one hand on top of your thigh.",
      "By using your thigh to help you get the dumbbell up, clean the dumbbell up so that you can hold i...",
      "Once at shoulder width, rotate your wrist forward so that the palm of your hand is facing away fr...",
      "Bring down the weights slowly to your side as you breathe in.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "one_arm_dumbbell_preacher_curl",
    dbId: "One_Arm_Dumbbell_Preacher_Curl",
    name: "One Arm Dumbbell Preacher Curl",
    primary: ["biceps"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the biceps.",
    cues: [
      "Grab a dumbbell with the right arm and place the upper arm on top of the preacher bench or the in...",
      "As you breathe in, slowly lower the dumbbell until your upper arm is extended and the biceps is f...",
      "As you exhale, use the biceps to curl the weight up until your biceps is fully contracted and the...",
      "Squeeze the biceps hard for a second at the contracted position and repeat for the recommended am...",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "one_arm_pronated_dumbbell_triceps_extension",
    dbId: "One_Arm_Pronated_Dumbbell_Triceps_Extension",
    name: "One Arm Pronated Dumbbell Triceps Extension",
    primary: ["triceps"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the triceps.",
    cues: [
      "Lie flat on a bench while holding a dumbbell at arms length.",
      "Place your non lifting hand on your bicep for support.",
      "Slowly begin to lower the dumbbell down as you breathe in.",
      "Then, begin lifting the dumbbell upward as you contract the triceps.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "one_arm_supinated_dumbbell_triceps_extension",
    dbId: "One_Arm_Supinated_Dumbbell_Triceps_Extension",
    name: "One Arm Supinated Dumbbell Triceps Extension",
    primary: ["triceps"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the triceps.",
    cues: [
      "Lie flat on a bench while holding a dumbbell at arms length.",
      "Place your non lifting hand on your bicep for support.",
      "Slowly begin to lower the dumbbell down as you breathe in.",
      "Then, begin lifting the dumbbell upward as you contract the triceps.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "palms_down_dumbbell_wrist_curl_over_a_bench",
    dbId: "Palms-Down_Dumbbell_Wrist_Curl_Over_A_Bench",
    name: "Palms-Down Dumbbell Wrist Curl Over A Bench",
    primary: ["forearms"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the forearms.",
    cues: [
      "Start out by placing two dumbbells on one side of a flat bench.",
      "Kneel down on both of your knees so that your body is facing the flat bench.",
      "Use your arms to grab both of the dumbbells with a pronated grip (palms facing down) and bring th...",
      "Start out by curling your wrist upwards and exhaling.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "palms_up_dumbbell_wrist_curl_over_a_bench",
    dbId: "Palms-Up_Dumbbell_Wrist_Curl_Over_A_Bench",
    name: "Palms-Up Dumbbell Wrist Curl Over A Bench",
    primary: ["forearms"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the forearms.",
    cues: [
      "Start out by placing two dumbbells on one side of a flat bench.",
      "Kneel down on both of your knees so that your body is facing the flat bench.",
      "Use your arms to grab both of the dumbbells with a supinated grip (palms up) and bring them up so...",
      "Start out by curling your wrist upwards and exhaling.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "power_partials",
    dbId: "Power_Partials",
    name: "Power Partials",
    primary: ["shoulders"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the shoulders.",
    cues: [
      "Stand up with your torso upright and a dumbbell on each hand being held at arms length.",
      "The palms of the hands should be facing your torso.",
      "Keeping your arms straight and the torso stationary, lift the weights out to your sides until the...",
      "Feel the contraction for a second and begin to lower the weights back down to the starting positi...",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "preacher_hammer_dumbbell_curl",
    dbId: "Preacher_Hammer_Dumbbell_Curl",
    name: "Preacher Hammer Dumbbell Curl",
    primary: ["biceps"],
    secondary: ["forearms"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the biceps.",
    cues: [
      "Place the upper part of both arms on top of the preacher bench as you hold a dumbbell in each han...",
      "As you breathe in, slowly lower the dumbbells until your upper arm is extended and the biceps is...",
      "As you exhale, use the biceps to curl the weight up until your biceps is fully contracted and the...",
      "Squeeze the biceps hard for a second at the contracted position and repeat for the recommended am...",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "reverse_flyes_with_external_rotation",
    dbId: "Reverse_Flyes_With_External_Rotation",
    name: "Reverse Flyes With External Rotation",
    primary: ["shoulders"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell isolation movement targeting the shoulders.",
    cues: [
      "To begin, lie down on an incline bench set at a 30-degree angle with the chest and stomach pressi...",
      "Have the dumbbells in each hand with the palms facing down to the floor.",
      "Maintaining the slight bend of the elbows, move the weights out and away from each other in an ar...",
      "As you lift the weight, your wrist should externally rotate by 90-degrees so that you go from a p...",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "seated_bent_over_one_arm_dumbbell_triceps_extension",
    dbId: "Seated_Bent-Over_One-Arm_Dumbbell_Triceps_Extension",
    name: "Seated Bent-Over One-Arm Dumbbell Triceps Extension",
    primary: ["triceps"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the triceps.",
    cues: [
      "Sit down at the end of a flat bench with a dumbbell in one arm using a neutral grip (palms of the...",
      "Bend your knees slightly and bring your torso forward, by bending at the waist, while keeping the...",
      "The upper arm with the dumbbell should be close to the torso and aligned with it (lifted up until...",
      "Keeping the upper arm stationary, use the triceps to lift the weight as you exhale until the fore...",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "seated_bent_over_rear_delt_raise",
    dbId: "Seated_Bent-Over_Rear_Delt_Raise",
    name: "Seated Bent-Over Rear Delt Raise",
    primary: ["shoulders"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell isolation movement targeting the shoulders.",
    cues: [
      "Place a couple of dumbbells looking forward in front of a flat bench.",
      "Sit on the end of the bench with your legs together and the dumbbells behind your calves.",
      "Bend at the waist while keeping the back straight in order to pick up the dumbbells.",
      "Keeping your torso forward and stationary, and the arms slightly bent at the elbows, lift the dum...",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "seated_bent_over_two_arm_dumbbell_triceps_extension",
    dbId: "Seated_Bent-Over_Two-Arm_Dumbbell_Triceps_Extension",
    name: "Seated Bent-Over Two-Arm Dumbbell Triceps Extension",
    primary: ["triceps"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell isolation movement targeting the triceps.",
    cues: [
      "Sit down at the end of a flat bench with a dumbbell in both arms using a neutral grip (palms of t...",
      "Bend your knees slightly and bring your torso forward, by bending at the waist, while keeping the...",
      "The upper arms with the dumbbells should be close to the torso and aligned with it (lifted up unt...",
      "Keeping the upper arms stationary, use the triceps to lift the weight as you exhale until the for...",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "seated_dumbbell_inner_biceps_curl",
    dbId: "Seated_Dumbbell_Inner_Biceps_Curl",
    name: "Seated Dumbbell Inner Biceps Curl",
    primary: ["biceps"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the biceps.",
    cues: [
      "Sit on the end of a flat bench with a dumbbell in each hand being held at arms length.",
      "Rotate the palms of the hands so that they are facing inward in a neutral position.",
      "While holding the upper arms stationary, curl the dumbbells out and up, turning the palms out as...",
      "Only the forearms should move.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "seated_dumbbell_palms_down_wrist_curl",
    dbId: "Seated_Dumbbell_Palms-Down_Wrist_Curl",
    name: "Seated Dumbbell Palms-Down Wrist Curl",
    primary: ["forearms"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the forearms.",
    cues: [
      "Start out by placing two dumbbells on the floor in front of a flat bench.",
      "Sit down on the edge of the flat bench with your legs at about shoulder width apart.",
      "Use your arms to grab both of the dumbbells and bring them up so that your forearms are resting a...",
      "Start out by curling your wrist upwards and exhaling.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "seated_dumbbell_palms_up_wrist_curl",
    dbId: "Seated_Dumbbell_Palms-Up_Wrist_Curl",
    name: "Seated Dumbbell Palms-Up Wrist Curl",
    primary: ["forearms"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the forearms.",
    cues: [
      "Start out by placing two dumbbells on the floor in front of a flat bench.",
      "Sit down on the edge of the flat bench with your legs at about shoulder width apart.",
      "Use your arms to grab both of the dumbbells and bring them up so that your forearms are resting a...",
      "Start out by curling your wrist upwards and exhaling.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "seated_dumbbell_press",
    dbId: "Seated_Dumbbell_Press",
    name: "Seated Dumbbell Press",
    primary: ["shoulders"],
    secondary: ["triceps"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell compound movement targeting the shoulders.",
    cues: [
      "Grab a couple of dumbbells and sit on a military press bench or a utility bench that has a back s...",
      "Clean the dumbbells up one at a time by using your thighs to bring the dumbbells up to shoulder h...",
      "Rotate the wrists so that the palms of your hands are facing forward.",
      "As you exhale, push the dumbbells up until they touch at the top.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "seated_one_arm_dumbbell_palms_down_wrist_curl",
    dbId: "Seated_One-Arm_Dumbbell_Palms-Down_Wrist_Curl",
    name: "Seated One-Arm Dumbbell Palms-Down Wrist Curl",
    primary: ["forearms"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell isolation movement targeting the forearms.",
    cues: [
      "Sit on a flat bench with a dumbbell in your right hand.",
      "Place your feet flat on the floor, at a distance that is slightly wider than shoulder width apart.",
      "Lean forward and place your right forearm on top of your upper right thigh with your palm down.",
      "Lower the dumbbell as far as possible as you keep a tight grip on the dumbbell.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "seated_one_arm_dumbbell_palms_up_wrist_curl",
    dbId: "Seated_One-Arm_Dumbbell_Palms-Up_Wrist_Curl",
    name: "Seated One-Arm Dumbbell Palms-Up Wrist Curl",
    primary: ["forearms"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the forearms.",
    cues: [
      "Sit on a flat bench with a dumbbell in your right hand.",
      "Place your feet flat on the floor, at a distance that is slightly wider than shoulder width apart.",
      "Lean forward and place your right forearm on top of your upper right thigh with your palm up.",
      "Lower the dumbbell as far as possible as you keep a tight grip on the dumbbell.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "seated_side_lateral_raise",
    dbId: "Seated_Side_Lateral_Raise",
    name: "Seated Side Lateral Raise",
    primary: ["shoulders"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the shoulders.",
    cues: [
      "Pick a couple of dumbbells and sit at the end of a flat bench with your feet firmly on the floor.",
      "While maintaining the torso stationary (no swinging), lift the dumbbells to your side with a slig...",
      "Lower the dumbbells back down slowly to the starting position as you inhale.",
      "Repeat for the recommended amount of repetitions.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "seated_triceps_press",
    dbId: "Seated_Triceps_Press",
    name: "Seated Triceps Press",
    primary: ["triceps"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the triceps.",
    cues: [
      "Sit down on a bench with back support and grasp a dumbbell with both hands and hold it overhead a...",
      "Keeping your upper arms close to your head (elbows in) and perpendicular to the floor, lower the...",
      "Go back to the starting position by using the triceps to raise the dumbbell.",
      "Repeat for the recommended amount of repetitions.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "see_saw_press_alternating_side_press",
    dbId: "See-Saw_Press_Alternating_Side_Press",
    name: "See-Saw Press (Alternating Side Press)",
    primary: ["shoulders"],
    secondary: ["abs", "triceps"],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell compound movement targeting the shoulders.",
    cues: [
      "Grab a dumbbell with each hand and stand up erect.",
      "Clean (lift) the dumbbells to the chest/shoulder level and then rotate your wrists so that your p...",
      "Now start extending your left arm overhead as you rotate the wrist so that the palm of your hand...",
      "Once you reach the top position breathe in.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "side_laterals_to_front_raise",
    dbId: "Side_Laterals_to_Front_Raise",
    name: "Side Laterals to Front Raise",
    primary: ["shoulders"],
    secondary: ["traps"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the shoulders.",
    cues: [
      "In a standing position, hold a pair of dumbbells at your side.",
      "Keeping your elbows slightly bent, raise the weights directly in front of you to shoulder height,...",
      "At the top of the exercise move the weights out in front of you, keeping your arms extended.",
      "Lower the weights with a controlled motion.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "single_dumbbell_raise",
    dbId: "Single_Dumbbell_Raise",
    name: "Single Dumbbell Raise",
    primary: ["shoulders"],
    secondary: ["forearms", "traps"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the shoulders.",
    cues: [
      "With a wide stance, hold a dumbell with both hands, grasping the head of the dumbbell instead of...",
      "Raise the weight until it is above shoulder level, keeping your arms extended.",
      "Return to the starting position and repeat for the recommended amount of repetitions.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "spell_caster",
    dbId: "Spell_Caster",
    name: "Spell Caster",
    primary: ["abs"],
    secondary: ["glutes", "shoulders"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell compound movement targeting the abs.",
    cues: [
      "Hold a dumbbell in each hand with a pronated grip.",
      "Begin the movement by pulling both of the dumbbells to one side next to your hip, rotating your t...",
      "Keeping your arms straight and the dumbbells parallel to the ground, rotate your torso to swing t...",
      "Continue alternating, rotating from one side to the other until the set is complete.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "standing_alternating_dumbbell_press",
    dbId: "Standing_Alternating_Dumbbell_Press",
    name: "Standing Alternating Dumbbell Press",
    primary: ["shoulders"],
    secondary: ["triceps"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell compound movement targeting the shoulders.",
    cues: [
      "Stand with a dumbbell in each hand.",
      "Extend one arm to press the dumbbell straight up, keeping your off hand in place.",
      "After a brief pause, return the weight to the starting position.",
      "Repeat for the opposite side, continuing to alternate between arms.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "standing_bent_over_one_arm_dumbbell_triceps_extension",
    dbId: "Standing_Bent-Over_One-Arm_Dumbbell_Triceps_Extension",
    name: "Standing Bent-Over One-Arm Dumbbell Triceps Extension",
    primary: ["triceps"],
    secondary: ["shoulders"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the triceps.",
    cues: [
      "With a dumbbell in one hand and the palm facing your torso, bend your knees slightly and bring yo...",
      "The upper arm should be close to the torso and parallel to the floor while the forearm is pointin...",
      "Keeping the upper arms stationary, use the triceps to lift the weights as you exhale until the fo...",
      "After a second contraction at the top, slowly lower the dumbbell back to the starting position as...",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "standing_bent_over_two_arm_dumbbell_triceps_extension",
    dbId: "Standing_Bent-Over_Two-Arm_Dumbbell_Triceps_Extension",
    name: "Standing Bent-Over Two-Arm Dumbbell Triceps Extension",
    primary: ["triceps"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the triceps.",
    cues: [
      "With a dumbbell in each hand and the palms facing your torso, bend your knees slightly and bring...",
      "Keeping the upper arms stationary, use the triceps to lift the weights as you exhale until the fo...",
      "After a second contraction at the top, slowly lower the dumbbells back to their starting position...",
      "Repeat the movement for the prescribed amount of repetitions.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "standing_concentration_curl",
    dbId: "Standing_Concentration_Curl",
    name: "Standing Concentration Curl",
    primary: ["biceps"],
    secondary: ["forearms"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the biceps.",
    cues: [
      "Taking a dumbbell in your working hand, lean forward.",
      "Flex the elbow to curl the weight, keeping the upper arm stationary.",
      "Lower the dumbbell back to the starting position.",
      "Repeat the movement for the prescribed amount of repetitions.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "standing_dumbbell_press",
    dbId: "Standing_Dumbbell_Press",
    name: "Standing Dumbbell Press",
    primary: ["shoulders"],
    secondary: ["triceps"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell compound movement targeting the shoulders.",
    cues: [
      "Standing with your feet shoulder width apart, take a dumbbell in each hand.",
      "Maintaining strict technique with no leg drive or leaning back, extend through the elbow to raise...",
      "Pause, and slowly return the weight to the starting position.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "standing_dumbbell_reverse_curl",
    dbId: "Standing_Dumbbell_Reverse_Curl",
    name: "Standing Dumbbell Reverse Curl",
    primary: ["biceps"],
    secondary: ["forearms"],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell isolation movement targeting the biceps.",
    cues: [
      "To begin, stand straight with a dumbbell in each hand using a pronated grip (palms facing down).",
      "While holding the upper arms stationary, curl the weights while contracting the biceps as you bre...",
      "Slowly begin to bring the dumbbells back to starting position as your breathe in.",
      "Repeat for the recommended amount of repetitions.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "standing_dumbbell_straight_arm_front_delt_raise_above_head",
    dbId: "Standing_Dumbbell_Straight-Arm_Front_Delt_Raise_Above_Head",
    name: "Standing Dumbbell Straight-Arm Front Delt Raise Above Head",
    primary: ["shoulders"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell isolation movement targeting the shoulders.",
    cues: [
      "Hold the dumbbells in front of your thighs, palms facing your thighs.",
      "Keep your arms straight with a slight bend at the elbows but keep them locked.",
      "Raise the dumbbells in a semicircular motion to arm's length overhead as you exhale.",
      "Slowly return to the starting position using the same path as you inhale.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "standing_dumbbell_upright_row",
    dbId: "Standing_Dumbbell_Upright_Row",
    name: "Standing Dumbbell Upright Row",
    primary: ["shoulders", "traps"],
    secondary: ["biceps"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell compound movement targeting the traps.",
    cues: [
      "Grasp a dumbbell in each hand with a pronated (palms forward) grip that is slightly less than sho...",
      "Use your side shoulders to lift the dumbbells as you exhale.",
      "Lower the dumbbells back down slowly to the starting position.",
      "Repeat for the recommended amount of repetitions.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "standing_inner_biceps_curl",
    dbId: "Standing_Inner-Biceps_Curl",
    name: "Standing Inner-Biceps Curl",
    primary: ["biceps"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell isolation movement targeting the biceps.",
    cues: [
      "Stand up with a dumbbell in each hand being held at arms length.",
      "Rotate the palms of the hands so that they are facing inward in a neutral position.",
      "While holding the upper arms stationary, curl the weights out while contracting the biceps as you...",
      "Only the forearms should move.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "standing_one_arm_dumbbell_curl_over_incline_bench",
    dbId: "Standing_One-Arm_Dumbbell_Curl_Over_Incline_Bench",
    name: "Standing One-Arm Dumbbell Curl Over Incline Bench",
    primary: ["biceps"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the biceps.",
    cues: [
      "Stand on the back side of an incline bench as if you were going to be a spotter for someone.",
      "Position your non lifting hand at the corner or side of the incline bench.",
      "While holding the upper arm stationary, curl the dumbbell upward while contracting the biceps as...",
      "Slowly begin to bring the dumbbells back to starting position as your breathe in.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "standing_one_arm_dumbbell_triceps_extension",
    dbId: "Standing_One-Arm_Dumbbell_Triceps_Extension",
    name: "Standing One-Arm Dumbbell Triceps Extension",
    primary: ["triceps"],
    secondary: ["shoulders"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the triceps.",
    cues: [
      "To begin, stand up with a dumbbell held in one hand.",
      "This will be your starting position.",
      "Keeping your upper arm close to your head (elbows in) and perpendicular to the floor, lower the r...",
      "Go back to the starting position by using the triceps to raise the dumbbell.",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "standing_palm_in_one_arm_dumbbell_press",
    dbId: "Standing_Palm-In_One-Arm_Dumbbell_Press",
    name: "Standing Palm-In One-Arm Dumbbell Press",
    primary: ["shoulders"],
    secondary: ["triceps"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell compound movement targeting the shoulders.",
    cues: [
      "Start by having a dumbbell in one hand with your arm fully extended to the side using a neutral g...",
      "Your feet should be shoulder width apart from each other.",
      "Slowly lift the dumbbell up until your arm is fully extended.",
      "While inhaling lower the weight down until your arm is at a 90 degree angle again.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "standing_palms_in_dumbbell_press",
    dbId: "Standing_Palms-In_Dumbbell_Press",
    name: "Standing Palms-In Dumbbell Press",
    primary: ["shoulders"],
    secondary: ["triceps"],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell compound movement targeting the shoulders.",
    cues: [
      "Start by having a dumbbell in each hand with your arm fully extended to the side using a neutral...",
      "Continue to maintain a neutral grip throughout the entire exercise.",
      "While inhaling lower the weights down until your arm is at a 90 degree angle again.",
      "Repeat for the recommended amount of repetitions.",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "straight_arm_dumbbell_pullover",
    dbId: "Straight-Arm_Dumbbell_Pullover",
    name: "Straight-Arm Dumbbell Pullover",
    primary: ["chest"],
    secondary: ["back", "shoulders", "triceps"],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell compound movement targeting the chest.",
    cues: [
      "Place a dumbbell standing up on a flat bench.",
      "Ensuring that the dumbbell stays securely placed at the top of the bench, lie perpendicular to th...",
      "Grasp the dumbbell with both hands and hold it straight over your chest at arms length.",
      "While keeping your arms straight, lower the weight slowly in an arc behind your head while breath...",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "tate_press",
    dbId: "Tate_Press",
    name: "Tate Press",
    primary: ["triceps"],
    secondary: ["chest", "shoulders"],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell isolation movement targeting the triceps.",
    cues: [
      "Lie down on a flat bench with a dumbbell in each hand on top of your thighs.",
      "By using your thighs to help you get the dumbbells up, clean the dumbbells one arm at a time so t...",
      "Keeping the upper arms stationary, slowly move the dumbbells in and down in a semi circular motio...",
      "As you breathe out, move the dumbbells up using your triceps and the same semi-circular motion bu...",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "two_arm_dumbbell_preacher_curl",
    dbId: "Two-Arm_Dumbbell_Preacher_Curl",
    name: "Two-Arm Dumbbell Preacher Curl",
    primary: ["biceps"],
    secondary: [],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell isolation movement targeting the biceps.",
    cues: [
      "Grab a dumbbell with each arm and place the upper arms on top of the preacher bench or the inclin...",
      "As you breathe in, slowly lower the dumbbells until your upper arm is extended and the biceps is...",
      "As you exhale, use the biceps to curl the weights up until your biceps is fully contracted and th...",
      "Squeeze the biceps hard for a second at the contracted position and repeat for the recommended am...",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
  {
    id: "vertical_swing",
    dbId: "Vertical_Swing",
    name: "Vertical Swing",
    primary: ["hamstrings"],
    secondary: ["glutes", "quads", "shoulders"],
    equipment: "Dumbbells",
    difficulty: "Beginner",
    description: "Beginner dumbbell compound movement targeting the hamstrings.",
    cues: [
      "Allow the dumbbell to hang at arms length between your legs, holding it with both hands.",
      "Swing the dumbbell between your legs, flexing at the hips and bending the knees slightly.",
      "Powerfully reverse the motion by extending at the hips, knees, and ankles to propel yourself upwa...",
      "As you land, absorb the impact through your legs and draw the dumbbell to your torso before the n...",
    ],
    sets: "4 × 8–12 reps",
    rest: "90 sec",
  },
  {
    id: "zottman_preacher_curl",
    dbId: "Zottman_Preacher_Curl",
    name: "Zottman Preacher Curl",
    primary: ["biceps"],
    secondary: ["forearms"],
    equipment: "Dumbbells",
    difficulty: "Intermediate",
    description: "Intermediate dumbbell isolation movement targeting the biceps.",
    cues: [
      "Grab a dumbbell in each hand and place your upper arms on top of the preacher bench or the inclin...",
      "As you breathe in, slowly lower the dumbbells keeping the palms down until your upper arm is exte...",
      "Now rotate your wrists once you are at the bottom of the movement so that the palms of the hands...",
      "As you exhale, use your biceps to curl the weights up until they are fully contracted and the dum...",
    ],
    sets: "3 × 10–15 reps",
    rest: "60 sec",
  },
];

const MAPPED_EXERCISES: Array<Exercise> = RAW_EXERCISES.map((raw) =>
  Object.assign({}, raw, { id: ExerciseId(raw.id) }),
);

const [FIRST_EXERCISE_VALUE, ...REST_EXERCISES] = MAPPED_EXERCISES;
if (FIRST_EXERCISE_VALUE === undefined) throw new Error("Exercise catalog is empty");

export const EXERCISES: readonly [Exercise, ...Array<Exercise>] = [
  FIRST_EXERCISE_VALUE,
  ...REST_EXERCISES,
];

/**
 * Prefix marking a user-authored exercise id. Lets sync call sites tell custom
 * exercises apart from the bundled catalog without a per-exercise flag.
 */
export const CUSTOM_EXERCISE_ID_PREFIX = "custom_";

/**
 * Runtime overlay of the bundled catalog with the signed-in user's custom
 * exercises. Kept as module state so the many synchronous resolvers
 * (`findExercise`, `resolveExercises`, workout rows, 3D, coverage) see custom
 * exercises without threading an async catalog through every caller. The single
 * writer is `setCustomExercises`, driven by the `useCustomExercises` hook.
 */
let CUSTOM_EXERCISES: ReadonlyArray<Exercise> = [];

export const setCustomExercises = (list: ReadonlyArray<Exercise>): void => {
  CUSTOM_EXERCISES = list;
};

export const getAllExercises = (): ReadonlyArray<Exercise> =>
  CUSTOM_EXERCISES.length === 0 ? EXERCISES : [...EXERCISES, ...CUSTOM_EXERCISES];

export const isCustomExerciseId = (id: ExerciseId): boolean =>
  id.startsWith(CUSTOM_EXERCISE_ID_PREFIX);

export const findExercise = (id: ExerciseId): Exercise | undefined =>
  EXERCISES.find((exercise) => exercise.id === id) ??
  CUSTOM_EXERCISES.find((exercise) => exercise.id === id);
